// Stepped-wizard `/campaigns/new`. Five steps — Identity, Audience,
// Sequence, Goals + AI, Review — with strict per-step validation, gated
// forward, free back, and a one-way-door launch confirm.
//
// Two modes:
//   * create — `/campaigns/new`. No campaign id yet. Form starts blank
//     (or rehydrated from localStorage). "Save as draft" POSTs a new
//     row, "Launch" POSTs then transitions to active.
//   * edit  — `/campaigns/:id/edit-wizard`. Loads the campaign, hydrates
//     the form from the response, and "Save as draft" / "Launch" call
//     PUT /campaigns/:id (plus transition for launch). LocalStorage is
//     bypassed in edit mode — the server is the source of truth.
//
// Persistence model (create mode): backend `POST /campaigns` requires
// sequence_id, so we don't auto-save server-side step by step (no draft
// id exists yet). We mirror form state to localStorage on every change
// so the user keeps work across reloads, and we materialise the row on
// "Save as draft" or "Launch". The "Saved" badge reflects the
// localStorage write, not a server round-trip.
//
// Out-of-band fields (cadence skip-rules, schedule-for-later, collaborators,
// notify-on-first-reply) are kept locally only; they don't have backend
// columns yet and the prototype identifies them as visual-first. Cost
// estimate is computed client-side; the design hand-off explicitly flags
// this is a stand-in until a server `pricing.estimate` endpoint exists.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDownToLine, ArrowLeft, CalendarCheck, Check, ChevronRight, ClipboardCheck,
  Mail, MessageSquare, Mic, Phone, Plus, Sparkles, Trash2, UserPlus, X,
} from 'lucide-react';

import PageShell from '../../components/PageShell';
import ContactPickerModal from '../../components/ContactPickerModal';
import WebTestAgentModal from '../../components/WebTestAgentModal';
import {
  Button,
  Callout,
  Card,
  Checkbox,
  IconButton,
  Input,
  Pill,
  Select,
  Tabs,
  Textarea,
} from '../../ui';
import { useAuth } from '../../context/AuthContext';

import { campaignsAPI, extractCampaignApiError } from '../../services/campaignsAPI';
import { contactListsAPI } from '../../services/contactListsAPI';
import {
  creditsAPI, emailAPI, smsAPI,
  type CreditRates, type EmailTemplate, type SmsTemplate,
} from '../../services/api';
import { bookingPagesAPI } from '../../services/calendarsService';
import type { BookingPage } from '../../types/calendar';
import { agentsService } from '../../services/agentsService';
import type { Agent } from '../../types/agent';

import type { ContactList } from '../../types/contactList';
import type { Contact } from '../../types/contact';
import { createStaticListFromContacts, pickedListName } from '../../utils/audienceList';
import type {
  Campaign,
  CampaignCreatePayload,
  CampaignFieldDef,
  CampaignKpiTarget,
  CampaignType,
  CampaignUpdatePayload,
} from '../../types/campaign';
import {
  CampaignOutputSchemaEditor,
  normalizeOutputSchema,
} from './CampaignOutputSchema';

// ---------------------------------------------------------------------------
// Step model
// ---------------------------------------------------------------------------

// LT-413: 'goals_ai' replaced by 'brief'. The new step holds the L2
// per-offer brief (purpose / target_audience / call_objectives /
// likely_objections / scripts / kpis / additional_notes).
// LT-658: 'type' is the new first step (campaign product shape) and
// 'booking' is a conditional step shown only for call_with_booking.
type StepId =
  | 'type'
  | 'identity'
  | 'brief'
  | 'audience'
  | 'persona'
  | 'cadence'
  | 'booking'
  | 'review';

// LT-554 — wizard Persona step choices (symmetric agent creation).
type PersonaMode = 'create' | 'existing';

const DEFAULT_RETRY_COUNT = 3;
const DEFAULT_RETRY_DELAYS_DAYS = [1, 2, 7];

/** Resize the per-retry delays array to match a new count: truncate from
 * the right when shrinking, extend with the last value (or 1) when growing. */
function resizeRetryDelays(delays: number[], count: number): number[] {
  if (count <= delays.length) return delays.slice(0, count);
  const fill = delays.length > 0 ? delays[delays.length - 1] : 1;
  return [...delays, ...Array(count - delays.length).fill(fill)];
}

interface StepDef {
  id: StepId;
  index: number;
  label: string;
  hint: string;
}

// LT-413 (LT-405 §8.4): wizard reordered. Identity now carries
// campaign_goals (the strategic outcome). Step 2 is the Campaign Brief
// — the per-offer L2 fields LT-407 added on `campaigns`. Audience moves
// to step 3, Sequence to step 4. Tone is L1 now (lives on the agent)
// and is NOT in the wizard surface.
//
// LT-658: 'type' is the new first step — the operator picks the campaign
// product shape (call_with_retry vs call_with_booking) before anything
// else, since it gates the conditional 'booking' step. 'booking' is only
// present for call_with_booking and configures the booking page + the
// post-call confirmation SMS/email. The list is built per-type so indices
// stay contiguous (navigation reads them positionally) — see `stepsFor`.
const STEP_TEMPLATE: Record<StepId, Omit<StepDef, 'index'>> = {
  type:     { id: 'type',     label: 'Campaign type',  hint: 'Call-only, or call + booking' },
  identity: { id: 'identity', label: 'Identity',       hint: 'Name + owner + goals' },
  brief:    { id: 'brief',    label: 'Campaign Brief',  hint: 'What we\'re saying & to whom' },
  audience: { id: 'audience', label: 'Audience',       hint: 'Who gets enrolled' },
  // LT-554: the Persona step decides which Agent makes the calls.
  persona:  { id: 'persona',  label: 'Persona',        hint: 'Which agent makes the calls' },
  cadence:  { id: 'cadence',  label: 'Retry Strategy', hint: 'How many times to retry a no-answer' },
  booking:  { id: 'booking',  label: 'Booking',        hint: 'Booking page + confirmation message' },
  review:   { id: 'review',   label: 'Review',         hint: 'Confirm + launch' },
};

// Step order. The 'booking' step is included only for call_with_booking.
const STEP_ORDER: StepId[] = [
  'type', 'identity', 'brief', 'audience', 'persona', 'cadence', 'booking', 'review',
];

/** Build the active step list for a campaign type. The 'booking' step is
 *  dropped for call_with_retry; indices are renumbered contiguously so the
 *  positional navigation (find by index ± 1) stays correct. */
function stepsFor(campaignType: CampaignType): StepDef[] {
  return STEP_ORDER
    .filter((id) => (id === 'booking' ? campaignType === 'call_with_booking' : true))
    .map((id, index) => ({ ...STEP_TEMPLATE[id], index }));
}

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

type AudienceMode = 'list' | 'filter' | 'json';

interface FilterRule {
  field: string;
  op: 'is' | 'is_not' | 'lt' | 'gt';
  value: string;
}

interface KpiCard {
  // Mirrors prototype's prefix/target/suffix UX, mapped onto the existing
  // {name, target_value, unit} backend shape. `name` carries the KPI label
  // (the editor exposes it as the prefix-less "what" the operator wants
  // tracked), and we collapse prefix/suffix back into `unit` at submit time.
  name: string;
  target: string;     // user keystrokes; parsed at submit.
  prefix: string;     // e.g. "$"
  suffix: string;     // e.g. "%", "meetings"
}

// LT-413 (LT-405 §8.4): per-offer brief draft. Replaces the legacy
// AiContextDraft. Tone is gone (now L1 on agent_versions); free-text
// "success metrics" retired in favour of the structured KPI shape.
interface CampaignBriefDraft {
  purpose: string;            // "Why we're calling"
  target_audience: string;
  call_objectives: string;
  // newline-delimited; collapsed to string[] at submit.
  likely_objections: string;
  scripts: string;
  additional_notes: string;
}

interface FormState {
  // LT-658 — campaign product shape. Picked on the first step; gates the
  // conditional booking step. Immutable once the campaign exists (never
  // sent on PUT).
  campaignType: CampaignType;
  // LT-658 — booking config (only meaningful for call_with_booking). A
  // confirmation channel is "on" when its enabled flag is true AND a
  // template is picked; buildPayload derives the *_template_id from that.
  bookingPageId: string;
  smsConfirmEnabled: boolean;
  smsTemplateId: string;
  emailConfirmEnabled: boolean;
  emailTemplateId: string;

  name: string;
  description: string;
  ownerName: string;          // visual label only (current user); no users-list endpoint.
  enrolmentClosesAt: string;  // datetime-local
  // LT-413: campaign_goals lives on the Identity step (the strategic
  // outcome anchor) — was previously absent from the wizard.
  campaignGoals: string;

  audienceMode: AudienceMode;
  listId: string;
  filters: FilterRule[];
  json: string;
  // Per-campaign opt-out of the daily cooldown (maps to the backend
  // bypass_daily_cooldown column). True = this campaign may touch a
  // contact more than once per day. Default false (cooldown on).
  bypassCooldown: boolean;

  retryCount: number;
  retryDelaysDays: number[];
  skipWeekends: boolean;
  windowStart: string;        // HH:mm
  windowEnd: string;

  kpis: KpiCard[];
  // LT-624 — "data to capture" (output) schema; LT-663 — "data to provide"
  // (input) schema. Both declared on the Persona step, seeded from the
  // selected agent's curated suggestions.
  outputSchema: CampaignFieldDef[];
  inputSchema: CampaignFieldDef[];
  brief: CampaignBriefDraft;

  // LT-554 — Persona step: which Agent makes the calls.
  personaMode: PersonaMode;
  existingAgentId: string;            // 'existing' mode pick (agent id)
  // 'create' mode sub-form — the L1 agent fields, symmetric with the
  // upload path's extracted-agent fields. Voice is gender+language enums;
  // the backend resolves the ElevenLabs voice_id (operators never see it).
  agentName: string;
  agentVoiceGender: 'female' | 'male';
  agentVoiceLanguage: 'en' | 'en_hi' | 'en_id';
  agentTone: string;
  agentPersona: string;
  agentObjectionStyle: string;

  scheduleMode: 'now' | 'later';
  launchAt: string;           // datetime-local
}

const FILTER_FIELDS: Array<{
  id: string;
  label: string;
  kind: 'enum' | 'number';
  options?: string[];
}> = [
  { id: 'lifecycle_stage', label: 'Lifecycle', kind: 'enum',
    options: ['cold', 'in_sequence', 'engaged', 'qualified', 'customer', 'dormant'] },
  { id: 'region',          label: 'Region',    kind: 'enum',
    options: ['AU', 'NZ', 'APAC', 'EU', 'US'] },
  { id: 'industry',        label: 'Industry',  kind: 'enum',
    options: ['Logistics', 'Healthcare', 'Retail', 'SaaS', 'Manufacturing'] },
  { id: 'employees',       label: 'Employees', kind: 'number' },
  { id: 'last_touched_d',  label: 'Last touched (days ago)', kind: 'number' },
];

const STORAGE_KEY = 'cc_campaign_wizard_draft_v1';

const blankForm = (ownerName: string): FormState => ({
  // LT-658 — default to the legacy call-only shape.
  campaignType: 'call_with_retry',
  bookingPageId: '',
  smsConfirmEnabled: false,
  smsTemplateId: '',
  emailConfirmEnabled: false,
  emailTemplateId: '',

  name: '',
  description: '',
  ownerName,
  enrolmentClosesAt: '',
  campaignGoals: '',

  audienceMode: 'list',
  listId: '',
  filters: [{ field: 'lifecycle_stage', op: 'is', value: 'qualified' }],
  json: '{\n  "lifecycle_stage": "qualified"\n}',
  bypassCooldown: false,

  retryCount: 3,
  retryDelaysDays: [1, 2, 7],
  skipWeekends: true,
  windowStart: '09:00',
  windowEnd: '17:00',

  kpis: [
    { name: 'meetings_booked', target: '25', prefix: '', suffix: 'count' },
  ],
  outputSchema: [],
  inputSchema: [],
  brief: {
    purpose: '',
    target_audience: '',
    call_objectives: '',
    likely_objections: '',
    scripts: '',
    additional_notes: '',
  },

  // Persona step defaults to picking an existing agent (the common path —
  // the agent's curated schema seeds the campaign). Falls back to 'create'
  // when the workspace has fewer than two agents (handled in StepPersona).
  personaMode: 'existing',
  existingAgentId: '',
  agentName: '',
  agentVoiceGender: 'female',
  agentVoiceLanguage: 'en',
  agentTone: '',
  agentPersona: '',
  agentObjectionStyle: '',

  scheduleMode: 'now',
  launchAt: '',
});

function loadStoredForm(ownerName: string): FormState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return blankForm(ownerName);
    const parsed = JSON.parse(raw) as Partial<FormState>;
    return { ...blankForm(ownerName), ...parsed, ownerName };
  } catch {
    return blankForm(ownerName);
  }
}

// ---------------------------------------------------------------------------
// Hydration: Campaign -> FormState (edit mode)
// ---------------------------------------------------------------------------

function isFilterRule(x: unknown): x is FilterRule {
  if (!x || typeof x !== 'object') return false;
  const r = x as Record<string, unknown>;
  return typeof r.field === 'string'
    && typeof r.op === 'string'
    && ['is', 'is_not', 'lt', 'gt'].includes(r.op as string)
    && (typeof r.value === 'string' || typeof r.value === 'number');
}

/** Reverse of `serialiseFilterRules` — pick a wizard audience mode and
 *  prefill its inputs from the Campaign's `audience_filter` blob. */
function rehydrateAudience(
  audienceFilter: Record<string, unknown> | null,
): Pick<FormState, 'audienceMode' | 'filters' | 'json'> {
  const blank = blankForm('');
  if (!audienceFilter || Object.keys(audienceFilter).length === 0) {
    return { audienceMode: blank.audienceMode, filters: blank.filters, json: blank.json };
  }
  // {rules: [...]} envelope — direct round-trip when every rule
  // matches the wizard's FilterRule shape.
  const rules = audienceFilter.rules;
  if (Array.isArray(rules) && rules.length > 0 && rules.every(isFilterRule)) {
    return {
      audienceMode: 'filter',
      filters: rules.map((r) => ({ ...r, value: String(r.value) })),
      json: blank.json,
    };
  }
  // Legacy `{field: value}` shape — collapse into filter mode with op='is'
  // when every key matches a known FILTER_FIELDS id; otherwise fall through
  // to JSON mode so the operator can edit the raw blob.
  const keys = Object.keys(audienceFilter);
  const knownIds = new Set(FILTER_FIELDS.map((f) => f.id));
  if (keys.length > 0 && keys.every((k) => knownIds.has(k))) {
    return {
      audienceMode: 'filter',
      filters: keys.map((k) => ({
        field: k,
        op: 'is' as const,
        value: String(audienceFilter[k] ?? ''),
      })),
      json: blank.json,
    };
  }
  return {
    audienceMode: 'json',
    filters: blank.filters,
    json: JSON.stringify(audienceFilter, null, 2),
  };
}

/** KPI round-trip. The wizard editor splits unit into prefix/suffix
 *  (`$` lives in prefix, `%`/`count`/`meetings` in suffix); the backend
 *  stores a single `unit` string. Treat '$' as a prefix marker, drop
 *  everything else into suffix. */
function rehydrateKpis(kpis: CampaignKpiTarget[] | null): KpiCard[] {
  if (!kpis || kpis.length === 0) return blankForm('').kpis;
  return kpis.map((k) => {
    const unit = (k.unit ?? 'count').trim();
    const isCurrency = unit === '$';
    return {
      name: k.name,
      target: String(k.target_value),
      prefix: isCurrency ? unit : '',
      suffix: isCurrency ? '' : unit,
    };
  });
}

// LT-413: rehydrate the brief from a Campaign — prefer the structured
// columns LT-407 added; fall back to the legacy ai_context bridge view
// for historical campaigns that haven't been re-saved yet.
function rehydrateBrief(c: Campaign): CampaignBriefDraft {
  const blank = blankForm('').brief;
  const ctx = c.ai_context && typeof c.ai_context === 'object' ? c.ai_context : null;
  const legacyText = (k: string): string => {
    if (!ctx) return '';
    return typeof ctx[k] === 'string' ? (ctx[k] as string) : '';
  };
  const legacyList = (k: string): string => {
    if (!ctx) return '';
    return Array.isArray(ctx[k])
      ? (ctx[k] as unknown[]).filter((x) => typeof x === 'string').join('\n')
      : '';
  };

  // Structured wins; legacy bridge value is the safety-net read.
  const purpose = c.purpose ?? legacyText('value_prop') ?? blank.purpose;
  const target_audience = c.target_audience ?? legacyText('target_persona') ?? blank.target_audience;
  const call_objectives = c.call_objectives ?? blank.call_objectives;
  const likely_objections = (c.likely_objections && c.likely_objections.length > 0)
    ? c.likely_objections.join('\n')
    : legacyList('common_objections') || legacyList('objections');
  const scripts = (c.scripts && c.scripts.length > 0)
    ? c.scripts.join('\n')
    : legacyList('scripts');
  const additional_notes = c.additional_notes ?? blank.additional_notes;
  return {
    purpose,
    target_audience,
    call_objectives,
    likely_objections,
    scripts,
    additional_notes,
  };
}

function formFromCampaign(c: Campaign, ownerName: string): FormState {
  const blank = blankForm(ownerName);
  const audience = c.contact_list_id
    ? { audienceMode: 'list' as const, listId: c.contact_list_id, filters: blank.filters, json: blank.json }
    : { listId: '', ...rehydrateAudience(c.audience_filter) };
  return {
    ...blank,
    // LT-658 — campaign_type is immutable; hydrate so the booking step shows
    // for an existing call_with_booking draft. The confirmation channels are
    // "on" when the campaign carries a template id for that channel.
    campaignType: (c.campaign_type ?? 'call_with_retry') as CampaignType,
    bookingPageId: c.booking_page_id ?? '',
    smsConfirmEnabled: Boolean(c.confirmation_sms_template_id),
    smsTemplateId: c.confirmation_sms_template_id ?? '',
    emailConfirmEnabled: Boolean(c.confirmation_email_template_id),
    emailTemplateId: c.confirmation_email_template_id ?? '',
    name: c.name,
    description: c.description ?? '',
    enrolmentClosesAt: c.enrolment_closes_at ? c.enrolment_closes_at.slice(0, 16) : '',
    campaignGoals: c.campaign_goals ?? '',
    audienceMode: audience.audienceMode,
    listId: audience.listId,
    filters: audience.filters,
    json: audience.json,
    retryCount: c.retry_count ?? DEFAULT_RETRY_COUNT,
    retryDelaysDays: c.retry_delays_days ?? DEFAULT_RETRY_DELAYS_DAYS,
    bypassCooldown: c.bypass_daily_cooldown ?? false,
    kpis: rehydrateKpis(c.kpis),
    outputSchema: normalizeOutputSchema(c.output_schema),
    inputSchema: normalizeOutputSchema(c.input_schema),
    brief: rehydrateBrief(c),
    // A campaign edits as "pick existing" — its pinned agent (if any) is the
    // selection; a campaign with no pinned agent forces an explicit pick.
    personaMode: 'existing',
    existingAgentId: c.agent_id ?? '',
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

interface FieldErr { field: string; msg: string; hard: boolean }

interface StepValidity {
  errs: FieldErr[];
  hardErrs: FieldErr[];
  valid: boolean;
}

function stepValidity(step: StepId, f: FormState, audienceCount: number): StepValidity {
  const errs: FieldErr[] = [];
  if (step === 'identity') {
    if (!f.name.trim()) errs.push({ field: 'name', msg: 'Name is required', hard: true });
    if (!f.ownerName.trim()) errs.push({ field: 'owner', msg: 'Owner is required', hard: true });
    if (!f.campaignGoals.trim()) errs.push({ field: 'campaignGoals', msg: 'Campaign goals are required', hard: true });
  }
  if (step === 'audience') {
    if (audienceCount <= 0) errs.push({ field: 'audience', msg: 'Audience must have at least 1 contact', hard: true });
    if (audienceCount > 5000) errs.push({ field: 'audience', msg: 'Large audience — review the cost estimate before launching', hard: false });
    if (f.audienceMode === 'json') {
      try { JSON.parse(f.json); }
      catch { errs.push({ field: 'audience', msg: 'JSON audience filter is invalid', hard: true }); }
    }
  }
  if (step === 'cadence') {
    if (f.retryDelaysDays.length !== f.retryCount) {
      errs.push({ field: 'cadence', msg: 'Retry delays must have one entry per retry', hard: true });
    }
  }
  if (step === 'booking') {
    // LT-658 — only relevant for call_with_booking; the step is hidden
    // otherwise so this branch is never reached for call_with_retry.
    if (!f.bookingPageId) {
      errs.push({ field: 'bookingPage', msg: 'Pick a booking page for the in-call calendar booking', hard: true });
    }
    const smsOn = f.smsConfirmEnabled && Boolean(f.smsTemplateId);
    const emailOn = f.emailConfirmEnabled && Boolean(f.emailTemplateId);
    if (!smsOn && !emailOn) {
      errs.push({
        field: 'confirmation',
        msg: 'Turn on at least one confirmation channel (SMS or email) and pick a template',
        hard: true,
      });
    }
    // An enabled channel with no template chosen is itself an error so the
    // operator can't leave a half-configured toggle.
    if (f.smsConfirmEnabled && !f.smsTemplateId) {
      errs.push({ field: 'smsTemplate', msg: 'Pick an SMS template or turn SMS confirmation off', hard: true });
    }
    if (f.emailConfirmEnabled && !f.emailTemplateId) {
      errs.push({ field: 'emailTemplate', msg: 'Pick an email template or turn email confirmation off', hard: true });
    }
  }
  if (step === 'persona') {
    // 'create' needs a name; 'existing' needs a pick.
    if (f.personaMode === 'create' && !f.agentName.trim()) {
      errs.push({ field: 'agentName', msg: 'Give the new agent a name', hard: true });
    }
    if (f.personaMode === 'existing' && !f.existingAgentId) {
      errs.push({ field: 'existingAgentId', msg: 'Pick an agent', hard: true });
    }
  }
  if (step === 'brief') {
    // LT-413: 'purpose' replaces 'value_prop' as the primary anchor.
    if (!f.brief.purpose.trim()) {
      errs.push({
        field: 'purpose',
        msg: "Purpose (\"Why we're calling\") is required so the agent knows what the call is about",
        hard: true,
      });
    }
    if (!f.brief.target_audience.trim()) {
      errs.push({ field: 'target_audience', msg: 'Target audience is required', hard: true });
    }
    if (!f.brief.call_objectives.trim()) {
      errs.push({ field: 'call_objectives', msg: 'Call objectives are required', hard: true });
    }
    // Objections, scripts, additional notes and KPIs stay optional.
    if (f.kpis.length === 0) errs.push({ field: 'kpis', msg: 'No KPIs to track', hard: false });
  }
  const hardErrs = errs.filter((e) => e.hard);
  return { errs, hardErrs, valid: hardErrs.length === 0 };
}

// ---------------------------------------------------------------------------
// Audience filter serialisation
// ---------------------------------------------------------------------------

/** Build the JSON object stored on `audience_filter` for filter-mode rules.
 *  When every rule uses `is`, collapse to the legacy `{field: value}` shape
 *  for backward compatibility. Otherwise emit a `{rules: [...]}` envelope.  */
function serialiseFilterRules(rules: FilterRule[]): Record<string, unknown> {
  const cleaned = rules.filter((r) => r.field && r.value !== '');
  if (cleaned.length === 0) return {};
  if (cleaned.every((r) => r.op === 'is')) {
    const out: Record<string, unknown> = {};
    for (const r of cleaned) {
      const f = FILTER_FIELDS.find((x) => x.id === r.field);
      out[r.field] = f?.kind === 'number' ? Number(r.value) : r.value;
    }
    return out;
  }
  return { rules: cleaned };
}

/** Parse the JSON-mode value, returning null on parse failure. */
function parseJsonAudience(raw: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Cost estimate (client-side, until pricing.estimate endpoint exists)
// ---------------------------------------------------------------------------

// LT-538 — campaign cost is expressed in CREDITS, derived from the
// workspace credit-rate settings (creditsAPI.getRates), not dollars.
interface CostBreakdown {
  callCount: number;
  emailCount: number;
  smsCount: number;
  totalTouches: number;
  voiceCredits: number;
  smsCredits: number;
  emailCredits: number;
  totalCredits: number;
}

// Calls are billed by duration (1 credit per `seconds_per_credit_normal`
// seconds). We assume an average call of 2 minutes for the estimate.
const AVG_CALL_SECONDS = 120;

// Sensible fallbacks mirror the backend SettingsService defaults so the
// estimate renders before /credits/rates resolves (or if it fails).
const DEFAULT_RATES: CreditRates = {
  seconds_per_credit_normal: 30,
  credits_per_sms: 0.1,
  credits_per_email: 0.1,
};

function creditsPerCall(secondsPerCredit: number): number {
  const s = secondsPerCredit > 0 ? secondsPerCredit : DEFAULT_RATES.seconds_per_credit_normal;
  return Math.ceil(AVG_CALL_SECONDS / s);
}

/** Format a credit amount: integers plain, fractional to 2dp. */
function formatCredits(n: number): string {
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2);
}

function estimateCost(
  audienceCount: number,
  retryCount: number,
  rates: CreditRates,
): CostBreakdown {
  // Implicit voice sequence: one initial call + `retryCount` retries per contact.
  const attemptsPerContact = 1 + retryCount;
  const callCount = audienceCount * attemptsPerContact;
  // Email/SMS are not part of the implicit voice sequence today, but the
  // estimate keeps their (rate-driven) lines so they light up if a future
  // sequence adds those touches.
  const emailCount = 0;
  const smsCount = 0;
  const voiceCredits = callCount * creditsPerCall(rates.seconds_per_credit_normal);
  const smsCredits = smsCount * rates.credits_per_sms;
  const emailCredits = emailCount * rates.credits_per_email;
  return {
    callCount, emailCount, smsCount,
    totalTouches: callCount + emailCount + smsCount,
    voiceCredits, smsCredits, emailCredits,
    totalCredits: voiceCredits + smsCredits + emailCredits,
  };
}

// ---------------------------------------------------------------------------
// AI token estimate (chars -> 4-char token approximation)
// ---------------------------------------------------------------------------

const AI_TOKEN_BUDGET = 8000;

function estimateBriefChars(brief: CampaignBriefDraft): number {
  return (
    brief.purpose
    + brief.target_audience
    + brief.call_objectives
    + brief.likely_objections
    + brief.scripts
    + brief.additional_notes
  ).length;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const CampaignWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);
  const { user } = useAuth();
  const ownerName = useMemo(() => {
    if (!user) return '';
    const first = (user.first_name || '').trim();
    const last = (user.last_name || '').trim();
    if (first || last) return `${first} ${last}`.trim();
    return user.email || '';
  }, [user]);

  const [form, setForm] = useState<FormState>(() =>
    isEdit ? blankForm(ownerName) : loadStoredForm(ownerName),
  );
  const [editLoading, setEditLoading] = useState(isEdit);
  const [editCampaign, setEditCampaign] = useState<Campaign | null>(null);
  // LT-658 — 'type' is the first step (campaign product shape).
  const [step, setStep] = useState<StepId>('type');
  // Steps the operator has actually landed on. The rail only flags "N issues"
  // on visited steps so a freshly-started campaign doesn't scream errors on
  // pages the user hasn't reached yet. Edit mode starts with all visited so
  // the rail reflects the loaded campaign's true state.
  const [visitedSteps, setVisitedSteps] = useState<Set<StepId>>(
    () => new Set<StepId>(isEdit ? STEP_ORDER : ['type']),
  );
  const [savedAt, setSavedAt] = useState<string>('just now');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // LT-543 — Test Agent (WebRTC) on the Review step. In create mode the
  // campaign must be persisted first (the test call needs a real campaign +
  // its implicit sequence); `persistedId` remembers that draft so a later
  // Save/Launch updates it instead of creating a duplicate.
  const [persistedId, setPersistedId] = useState<string | null>(null);
  const [testAgentOpen, setTestAgentOpen] = useState(false);
  const [testAgentId, setTestAgentId] = useState<string | null>(null);

  // Refresh ownerName once user resolves (AuthContext loads async).
  useEffect(() => {
    setForm((f) => (f.ownerName === ownerName ? f : { ...f, ownerName }));
  }, [ownerName]);

  // Record each step the operator lands on (drives the rail's issue badges).
  useEffect(() => {
    setVisitedSteps((prev) => (prev.has(step) ? prev : new Set(prev).add(step)));
  }, [step]);

  // Persist to localStorage on every change. Bypassed in edit mode —
  // the server is the source of truth and we don't want the edit-mode
  // form to leak into a future create-from-scratch flow.
  useEffect(() => {
    if (isEdit) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); }
    catch { /* quota / private mode — non-fatal */ }
  }, [form, isEdit]);

  // Hydrate from server when the route carries a campaign id.
  useEffect(() => {
    if (!isEdit || !editId) return;
    let cancelled = false;
    setEditLoading(true);
    setSubmitError(null);
    campaignsAPI.get(editId)
      .then((c) => {
        if (cancelled) return;
        setEditCampaign(c);
        setForm(formFromCampaign(c, ownerName));
      })
      .catch((err) => {
        if (cancelled) return;
        setSubmitError(extractCampaignApiError(err, 'Failed to load campaign.'));
      })
      .finally(() => { if (!cancelled) setEditLoading(false); });
    return () => { cancelled = true; };
    // ownerName only flows in once at hydration time; subsequent changes
    // shouldn't refetch the campaign. The standalone effect above
    // refreshes ownerName on the form when the user resolves later.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editId]);

  // Auto-save badge: tick on step change.
  const lastStepRef = useRef(step);
  useEffect(() => {
    if (lastStepRef.current !== step) {
      lastStepRef.current = step;
      setSavedAt('just now');
      const t = setTimeout(() => setSavedAt('a moment ago'), 2000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [step]);

  // ---- Loaded data ------------------------------------------------------
  const [contactLists, setContactLists] = useState<ContactList[]>([]);
  const [audienceCount, setAudienceCount] = useState<number>(0);
  const [audiencePreviewLoading, setAudiencePreviewLoading] = useState(false);

  // LT-534 — individual-contact picker for the audience step.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerBusy, setPickerBusy] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);

  // LT-538 — workspace credit rates drive the cost estimate (in credits).
  const [creditRates, setCreditRates] = useState<CreditRates>(DEFAULT_RATES);

  // LT-554 — Persona step: workspace agent catalogue + the workspace default
  // agent. `createdAgentIdRef` caches an agent created for this campaign so a
  // save→launch sequence doesn't create duplicates (ref so it's readable
  // within a single submit callback before the next render).
  const [agents, setAgents] = useState<Agent[]>([]);
  const createdAgentIdRef = useRef<string | null>(null);

  // LT-658 — booking step catalogue: active booking pages + all active SMS /
  // email templates (the confirmation templates confirm an already-booked
  // meeting, so we list ALL active templates, not only booking-bearing ones).
  // Loaded lazily the first time the booking step is reachable
  // (campaign_type === 'call_with_booking') so call_with_retry campaigns never
  // pay for the fetch. `bookingLoaded` guards against re-fetching.
  const [bookingPages, setBookingPages] = useState<BookingPage[]>([]);
  const [smsTemplates, setSmsTemplates] = useState<SmsTemplate[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  // `bookingLoaded` is the UI flag (set when the fetch resolves) that lets the
  // booking step tell "loading…" from "no booking pages". The fetch-once guard
  // is a ref so flipping the flag doesn't re-run the effect (a state guard in
  // the deps would cancel the in-flight fetch via the cleanup on the re-run).
  const [bookingLoaded, setBookingLoaded] = useState(false);
  const bookingFetchStartedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const agentRows = await agentsService.list({
          includeSystem: true, includeDeprecated: false,
        });
        if (cancelled) return;
        setAgents(agentRows);
      } catch { /* catalogue optional; persona step degrades to 'create' */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    contactListsAPI.list({ limit: 200 })
      .catch(() => [] as ContactList[])
      .then((lists) => {
        if (cancelled) return;
        setContactLists(lists);
      });
    creditsAPI.getRates()
      .then((r) => { if (!cancelled) setCreditRates(r); })
      .catch(() => { /* keep DEFAULT_RATES */ });
    return () => { cancelled = true; };
  }, []);

  // LT-658 — load the booking-step catalogue once the campaign is (or becomes)
  // a call_with_booking campaign. Active booking pages + all active SMS/email
  // templates. Fetched at most once (ref guard); the effect doesn't depend on
  // any state it sets, so it never re-runs and cancels its own in-flight fetch.
  useEffect(() => {
    if (form.campaignType !== 'call_with_booking' || bookingFetchStartedRef.current) {
      return undefined;
    }
    bookingFetchStartedRef.current = true;
    let cancelled = false;
    void (async () => {
      const [pages, sms, email] = await Promise.all([
        bookingPagesAPI.list({ is_active: true }).catch(() => [] as BookingPage[]),
        smsAPI.getTemplates().catch(() => [] as SmsTemplate[]),
        emailAPI.getTemplates().catch(() => [] as EmailTemplate[]),
      ]);
      if (cancelled) return;
      setBookingPages(pages);
      // The list endpoints return active + inactive; confirmation templates
      // must be active, so filter client-side (no is_active query param).
      setSmsTemplates(sms.filter((t) => t.is_active));
      setEmailTemplates(email.filter((t) => t.is_active));
      setBookingLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [form.campaignType]);

  // Audience preview: real count for `list` mode (contact-lists/evaluate),
  // best-effort heuristic for `filter` and `json` modes until a server-side
  // /campaigns/audience/preview lands.
  useEffect(() => {
    if (form.audienceMode === 'list') {
      if (!form.listId) { setAudienceCount(0); return; }
      let cancelled = false;
      setAudiencePreviewLoading(true);
      contactListsAPI.evaluate(form.listId)
        .then((r) => { if (!cancelled) setAudienceCount(r.member_count); })
        .catch(() => { if (!cancelled) setAudienceCount(0); })
        .finally(() => { if (!cancelled) setAudiencePreviewLoading(false); });
      return () => { cancelled = true; };
    }
    if (form.audienceMode === 'filter') {
      // Heuristic until the preview endpoint exists: 6000 contacts narrowed
      // by ~55% per applied rule. Keeps the UX responsive without a network
      // hit; the real count is computed at launch time.
      const ruleCount = form.filters.filter((r) => r.field && r.value !== '').length;
      setAudienceCount(Math.round(6000 * Math.pow(0.45, ruleCount)));
      return;
    }
    if (form.audienceMode === 'json') {
      const parsed = parseJsonAudience(form.json);
      if (!parsed) { setAudienceCount(0); return; }
      const k = Object.keys(parsed).length;
      setAudienceCount(Math.round(6000 * Math.pow(0.45, k)));
      return;
    }
    return;
  }, [form.audienceMode, form.listId, form.filters, form.json]);

  // ---- Patch helper -----------------------------------------------------
  const patch = useCallback((p: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...p }));
  }, []);

  // LT-534 — confirm the picked contacts: materialise them as a static list,
  // prepend it to the picker, and select it as the audience source. The
  // existing `list` mode handles count/preview/payload from here.
  const handlePickedContacts = useCallback(async (contacts: Contact[]) => {
    if (contacts.length === 0) return;
    setPickerBusy(true);
    setPickerError(null);
    try {
      const list = await createStaticListFromContacts(
        pickedListName(form.name, contacts.length),
        contacts.map((c) => c.id),
      );
      setContactLists((prev) => [list, ...prev]);
      patch({ audienceMode: 'list', listId: list.id });
      setPickerOpen(false);
    } catch (e) {
      setPickerError(
        e instanceof Error ? e.message : 'Failed to create list from selection',
      );
    } finally {
      setPickerBusy(false);
    }
  }, [form.name, patch]);

  // ---- Derived ----------------------------------------------------------
  // LT-658 — the active step list depends on campaign type (the booking
  // step is only present for call_with_booking). Computed once per type.
  const steps = useMemo(() => stepsFor(form.campaignType), [form.campaignType]);

  // If the campaign type flips away from call_with_booking while the operator
  // is on the (now-removed) booking step, fall back to the cadence step so we
  // never render against a step that isn't in the active list.
  useEffect(() => {
    if (!steps.some((s) => s.id === step)) {
      setStep('cadence');
    }
  }, [steps, step]);

  const validity = useMemo(
    () => stepValidity(step, form, audienceCount),
    [step, form, audienceCount],
  );
  const canGoForward = validity.valid;

  const cost = useMemo(
    () => estimateCost(audienceCount, form.retryCount, creditRates),
    [audienceCount, form.retryCount, creditRates],
  );

  // ---- Navigation -------------------------------------------------------
  const goNext = () => {
    if (!canGoForward) return;
    const next = steps.find((s) => s.index === steps.find((x) => x.id === step)!.index + 1);
    if (next) setStep(next.id);
  };
  const goBack = () => {
    const prev = steps.find((s) => s.index === steps.find((x) => x.id === step)!.index - 1);
    if (prev) setStep(prev.id);
  };

  // ---- Build payload ----------------------------------------------------
  const buildPayload = (opts: { forDraft?: boolean } = {}): CampaignCreatePayload | { error: string } => {
    const { forDraft = false } = opts;
    if (!forDraft && !form.name.trim()) return { error: 'Name is required' };

    // Drafts get an auto-name so they're identifiable in the campaigns list
    // even when the user hasn't typed one yet. They can rename later.
    const name = form.name.trim() ||
      `Untitled draft · ${new Date().toLocaleString()}`;
    const payload: CampaignCreatePayload = { name };
    payload.retry_count = form.retryCount;
    payload.retry_delays_days = form.retryDelaysDays;
    payload.bypass_daily_cooldown = form.bypassCooldown;

    // LT-658 — campaign type + booking config. campaign_type is always sent
    // on create (it is immutable after, so buildUpdatePayload strips it). The
    // booking fields are only meaningful for call_with_booking — the backend
    // 422s them on a call_with_retry campaign — so we only attach them for
    // that type, and leave them null otherwise.
    payload.campaign_type = form.campaignType;
    if (form.campaignType === 'call_with_booking') {
      if (form.bookingPageId) {
        payload.booking_page_id = form.bookingPageId;
      } else if (!forDraft) {
        return { error: 'Pick a booking page for the booking campaign.' };
      }
      // A confirmation channel is on only when both enabled AND a template is
      // chosen; otherwise the id is null (channel off).
      payload.confirmation_sms_template_id =
        form.smsConfirmEnabled && form.smsTemplateId ? form.smsTemplateId : null;
      payload.confirmation_email_template_id =
        form.emailConfirmEnabled && form.emailTemplateId ? form.emailTemplateId : null;
      if (
        !forDraft
        && !payload.confirmation_sms_template_id
        && !payload.confirmation_email_template_id
      ) {
        return { error: 'Pick at least one confirmation template (SMS or email).' };
      }
    }
    if (form.description.trim()) payload.description = form.description.trim();
    if (form.enrolmentClosesAt) {
      payload.enrolment_closes_at = new Date(form.enrolmentClosesAt).toISOString();
    }
    if (form.audienceMode === 'list' && form.listId) {
      payload.contact_list_id = form.listId;
    } else if (form.audienceMode === 'filter') {
      const filter = serialiseFilterRules(form.filters);
      if (Object.keys(filter).length > 0) payload.audience_filter = filter;
    } else if (form.audienceMode === 'json') {
      const parsed = parseJsonAudience(form.json);
      if (!parsed && !forDraft) return { error: 'JSON audience filter is invalid' };
      if (parsed) payload.audience_filter = parsed;
    }
    // KPIs — collapse the {prefix, target, suffix} editor shape into the
    // backend's {name, target_value, unit} shape.
    const kpis: CampaignKpiTarget[] = [];
    for (const k of form.kpis) {
      const target = parseFloat(k.target);
      if (!k.name.trim() || !Number.isFinite(target) || target <= 0) continue;
      const unit = (k.prefix.trim() || k.suffix.trim() || 'count');
      kpis.push({ name: k.name.trim(), target_value: target, unit });
    }
    if (kpis.length > 0) payload.kpis = kpis;

    // LT-624 — "data to capture" schema. Send the declared field defs
    // whenever any exist; the backend validates + normalises them.
    if (form.outputSchema.length > 0) payload.output_schema = form.outputSchema;
    // LT-663 — "data to provide" input schema. Same contract as output.
    if (form.inputSchema.length > 0) payload.input_schema = form.inputSchema;

    // LT-413 (LT-405 §8.4): Identity step's campaign_goals (the strategic
    // outcome anchor — renamed from goal_text in LT-407).
    if (form.campaignGoals.trim()) {
      payload.campaign_goals = form.campaignGoals.trim();
    }

    // LT-413: Campaign Brief — structured columns. Replaces the legacy
    // ai_context payload (LT-409 backend now 422s ai_context writes).
    if (form.brief.purpose.trim()) payload.purpose = form.brief.purpose.trim();
    if (form.brief.target_audience.trim()) {
      payload.target_audience = form.brief.target_audience.trim();
    }
    if (form.brief.call_objectives.trim()) {
      payload.call_objectives = form.brief.call_objectives.trim();
    }
    const objections = form.brief.likely_objections
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    if (objections.length > 0) payload.likely_objections = objections;
    const scripts = form.brief.scripts
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    if (scripts.length > 0) payload.scripts = scripts;
    if (form.brief.additional_notes.trim()) {
      payload.additional_notes = form.brief.additional_notes.trim();
    }

    // LT-554 — Persona: attach the resolved Agent override. Self-resolves
    // from persona state — 'create' reads the id the submit handler's
    // resolveAgentId() POSTed into the ref; 'existing' the catalogue pick.
    // Null/empty leaves it to the backend fallback (sequence-step resolution).
    const agentOverride =
      form.personaMode === 'create' ? createdAgentIdRef.current
      : (form.existingAgentId || null);
    if (agentOverride) payload.agent_id = agentOverride;

    return payload;
  };

  // CampaignUpdatePayload differs from CampaignCreatePayload only in that
  // contact_list_id is nullable (clearing the selection on edit). The rest
  // of the shape matches, so the build path is shared.
  const buildUpdatePayload = (opts: { forDraft?: boolean } = {}): CampaignUpdatePayload | { error: string } => {
    const built = buildPayload(opts);
    if ('error' in built) return built;
    // LT-658 — campaign_type is immutable; strip it from the PUT body so a
    // draft edit never tries to change the campaign's type (the backend would
    // reject it). The booking fields below stay — they're draft-editable.
    const { campaign_type: _campaignType, ...rest } = built;
    const update: CampaignUpdatePayload = { ...rest };
    // Form switched away from list mode: explicitly clear so the backend
    // drops any prior selection. (POST treats undefined as "no list"; PUT
    // treats undefined as "no change", so we have to send null.)
    if (form.audienceMode !== 'list') update.contact_list_id = null;
    // LT-624: send the capture schema unconditionally on edit so clearing ALL
    // fields round-trips. buildPayload omits output_schema when empty and PUT
    // treats an omitted field as "no change", which would otherwise silently
    // drop a full-remove. [] is a valid clear (validate_output_schema([]) -> []).
    update.output_schema = form.outputSchema;
    // LT-663 — same unconditional-send so clearing all input fields round-trips.
    update.input_schema = form.inputSchema;
    // LT-658 — for a call_with_booking campaign send the confirmation ids
    // unconditionally so toggling a channel OFF (id → null) round-trips. PUT
    // treats an omitted field as "no change", which would otherwise strand a
    // previously-set template. buildPayload already set them when the type is
    // call_with_booking; mirror null for the off case.
    if (form.campaignType === 'call_with_booking') {
      update.confirmation_sms_template_id =
        form.smsConfirmEnabled && form.smsTemplateId ? form.smsTemplateId : null;
      update.confirmation_email_template_id =
        form.emailConfirmEnabled && form.emailTemplateId ? form.emailTemplateId : null;
    }
    return update;
  };

  // LT-554 — resolve the campaign's Agent for the chosen Persona mode. For
  // 'create' it POSTs a workspace agent (cached in a ref so a save→launch
  // sequence doesn't create duplicates); 'existing' reads state.
  // Returns the agent id to attach (null = leave to the backend's fallback)
  // or an error string. Draft saves never block on an incomplete persona.
  const resolveAgentId = async (
    forDraft: boolean,
  ): Promise<{ agentId: string | null } | { error: string }> => {
    if (form.personaMode === 'existing') {
      if (form.existingAgentId) return { agentId: form.existingAgentId };
      return forDraft ? { agentId: null } : { error: 'Pick an agent for this campaign.' };
    }
    if (form.personaMode === 'create') {
      if (createdAgentIdRef.current) return { agentId: createdAgentIdRef.current };
      if (!form.agentName.trim()) {
        return forDraft ? { agentId: null } : { error: 'Give the new agent a name.' };
      }
      try {
        const agent = await agentsService.create({
          name: form.agentName.trim(),
          category: 'sales_retention',
          capabilities: ['end_call', 'lookup_info'],
          voice_gender: form.agentVoiceGender,
          voice_language: form.agentVoiceLanguage,
          tone: form.agentTone.trim() || null,
          agent_persona: form.agentPersona.trim() || null,
          objection_handling_style: form.agentObjectionStyle.trim() || null,
        });
        createdAgentIdRef.current = agent.id;
        return { agentId: agent.id };
      } catch (err) {
        return { error: extractCampaignApiError(err, 'Failed to create the agent.') };
      }
    }
    // No agent resolved (e.g. 'create' draft with no name yet) — leave it to
    // the backend's sequence-step fallback.
    return { agentId: null };
  };

  const saveDraft = async () => {
    setSubmitError(null);
    // LT-554: resolve (and, for 'create', POST) the persona agent first so
    // buildPayload can attach agent_id. Draft saves never block on this.
    const ag = await resolveAgentId(true);
    if ('error' in ag) { setSubmitError(ag.error); return; }
    if (isEdit && editCampaign) {
      const update = buildUpdatePayload({ forDraft: true });
      if ('error' in update) {
        setSubmitError(update.error);
        return;
      }
      setSubmitting(true);
      try {
        await campaignsAPI.update(editCampaign.id, update);
        navigate('/campaigns');
      } catch (err) {
        setSubmitError(extractCampaignApiError(err, 'Failed to save draft.'));
      } finally {
        setSubmitting(false);
      }
      return;
    }
    // LT-543: a draft already materialised (e.g. via "Test agent") — update
    // it instead of creating a duplicate.
    if (persistedId) {
      const update = buildUpdatePayload({ forDraft: true });
      if ('error' in update) {
        setSubmitError(update.error);
        return;
      }
      setSubmitting(true);
      try {
        await campaignsAPI.update(persistedId, update);
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
        navigate('/campaigns');
      } catch (err) {
        setSubmitError(extractCampaignApiError(err, 'Failed to save draft.'));
      } finally {
        setSubmitting(false);
      }
      return;
    }
    // Create mode: materialises a server row in `draft` status so the
    // draft shows up in the campaigns list and multiple drafts can
    // coexist (the previous localStorage-only path limited us to one
    // draft at a time and made the saved draft invisible from the list).
    const built = buildPayload({ forDraft: true });
    if ('error' in built) {
      setSubmitError(built.error);
      return;
    }
    setSubmitting(true);
    try {
      await campaignsAPI.create(built);
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      navigate('/campaigns');
    } catch (err) {
      setSubmitError(extractCampaignApiError(err, 'Failed to save draft.'));
    } finally {
      setSubmitting(false);
    }
  };

  // LT-543 — persist the campaign (create or update) and open the WebRTC
  // Test Agent modal. Mirrors CampaignFormPage's "Save & test" but works
  // within the wizard's create-or-edit flow.
  const handleTestAgent = async () => {
    setSubmitError(null);
    // LT-554: attach the persona agent to the draft we test against.
    const ag = await resolveAgentId(true);
    if ('error' in ag) { setSubmitError(ag.error); return; }
    setSubmitting(true);
    try {
      let id: string | null = null;
      if (isEdit && editCampaign) {
        const update = buildUpdatePayload({ forDraft: true });
        if ('error' in update) { setSubmitError(update.error); return; }
        await campaignsAPI.update(editCampaign.id, update);
        id = editCampaign.id;
      } else if (persistedId) {
        const update = buildUpdatePayload({ forDraft: true });
        if ('error' in update) { setSubmitError(update.error); return; }
        await campaignsAPI.update(persistedId, update);
        id = persistedId;
      } else {
        const built = buildPayload({ forDraft: true });
        if ('error' in built) { setSubmitError(built.error); return; }
        const created = await campaignsAPI.create(built);
        setPersistedId(created.id);
        id = created.id;
      }
      if (id) {
        setTestAgentId(id);
        setTestAgentOpen(true);
      }
    } catch (err) {
      setSubmitError(extractCampaignApiError(err, 'Failed to prepare campaign for test.'));
    } finally {
      setSubmitting(false);
    }
  };

  const launch = async () => {
    setSubmitError(null);
    // LT-554: persona agent is required at launch (create POSTs it now).
    const ag = await resolveAgentId(false);
    if ('error' in ag) { setSubmitError(ag.error); setConfirming(false); return; }
    if (isEdit && editCampaign) {
      const update = buildUpdatePayload();
      if ('error' in update) {
        setSubmitError(update.error);
        setConfirming(false);
        return;
      }
      setSubmitting(true);
      try {
        await campaignsAPI.update(editCampaign.id, update);
        // Transition draft -> active. 409 from the transition endpoint
        // surfaces back as the structured error; we leave the draft in
        // place so the user can retry from /campaigns/:id.
        await campaignsAPI.transition(editCampaign.id, 'active');
        navigate(`/campaigns/${editCampaign.id}`);
      } catch (err) {
        setSubmitError(extractCampaignApiError(err, 'Launch failed.'));
      } finally {
        setSubmitting(false);
        setConfirming(false);
      }
      return;
    }
    // LT-543: a draft already materialised (e.g. via "Test agent") — update +
    // transition it rather than creating a duplicate.
    if (persistedId) {
      const update = buildUpdatePayload();
      if ('error' in update) {
        setSubmitError(update.error);
        setConfirming(false);
        return;
      }
      setSubmitting(true);
      try {
        await campaignsAPI.update(persistedId, update);
        await campaignsAPI.transition(persistedId, 'active');
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
        navigate(`/campaigns/${persistedId}`);
      } catch (err) {
        setSubmitError(extractCampaignApiError(err, 'Launch failed.'));
      } finally {
        setSubmitting(false);
        setConfirming(false);
      }
      return;
    }
    const built = buildPayload();
    if ('error' in built) {
      setSubmitError(built.error);
      setConfirming(false);
      return;
    }
    setSubmitting(true);
    try {
      const created = await campaignsAPI.create(built);
      await campaignsAPI.transition(created.id, 'active');
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      navigate(`/campaigns/${created.id}`);
    } catch (err) {
      setSubmitError(extractCampaignApiError(err, 'Launch failed.'));
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  };

  // Guard: during the render right after a campaign-type flip the active
  // `step` may momentarily not be in `steps` (the reset effect runs next
  // tick). Fall back to the first step's index so indexing never blows up.
  const currentStepDef = steps.find((s) => s.id === step) ?? steps[0];
  const currentIndex = currentStepDef.index;

  if (editLoading) {
    return (
      <PageShell>
        <div className="text-sm text-slate-500 px-3 py-6">Loading…</div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* Top bar */}
      <header className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={() => navigate(isEdit && editCampaign ? `/campaigns/${editCampaign.id}` : '/campaigns')}
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
          >
            <ArrowLeft size={14} /> {isEdit ? 'Cockpit' : 'Campaigns'}
          </button>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
              <span className="w-1.5 h-1.5 rounded-full bg-ok" /> Saved · {savedAt}
            </span>
          </div>
        </div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">
          {isEdit ? 'campaigns · edit' : 'campaigns · new'}
        </div>
        <h1 className="text-2xl font-semibold text-slate-50">
          {isEdit && editCampaign
            ? `Edit draft: ${editCampaign.name}`
            : 'Create campaign'}
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          {step === 'review'
            ? 'Confirm everything below, then launch.'
            : `Step ${currentIndex + 1} of ${steps.length}: ${currentStepDef.label}. ${currentStepDef.hint}.`}
        </p>
      </header>

      {/* Submit error banner */}
      {submitError ? (
        <div
          role="alert"
          className="mb-4 px-3 py-2 rounded-ds-lg border border-danger-border bg-danger-tint text-danger-text text-sm"
        >
          {submitError}
        </div>
      ) : null}

      {/* Mobile rail */}
      <div className="lg:hidden mb-4">
        <MobileStepStrip
          steps={steps}
          step={step}
          setStep={setStep}
          form={form}
          audienceCount={audienceCount}
          canLeaveCurrent={canGoForward}
          visited={visitedSteps}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
        {/* Side rail (desktop) */}
        <div className="hidden lg:block">
          <div className="sticky top-4">
            <SideRail
              steps={steps}
              step={step}
              setStep={setStep}
              form={form}
              audienceCount={audienceCount}
              canLeaveCurrent={canGoForward}
              visited={visitedSteps}
            />
          </div>
        </div>

        {/* Main panel */}
        <Card className="min-h-[500px]">
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
            step {currentIndex + 1} of {steps.length}
          </div>
          <h2 className="text-xl font-semibold text-slate-50 mt-1">
            {currentStepDef.label}
          </h2>
          <p className="text-sm text-slate-400 mt-1 mb-5">
            {currentStepDef.hint}
          </p>

          {step === 'type' ? (
            <StepType form={form} patch={patch} errs={validity.errs} locked={isEdit} />
          ) : null}
          {step === 'identity' ? (
            <StepIdentity form={form} patch={patch} errs={validity.errs} />
          ) : null}
          {step === 'audience' ? (
            <StepAudience
              form={form}
              patch={patch}
              errs={validity.errs}
              contactLists={contactLists}
              audienceCount={audienceCount}
              audienceLoading={audiencePreviewLoading}
              onPickContacts={() => { setPickerError(null); setPickerOpen(true); }}
            />
          ) : null}
          {step === 'persona' ? (
            <StepPersona
              form={form}
              patch={patch}
              errs={validity.errs}
              agents={agents}
            />
          ) : null}
          {step === 'cadence' ? (
            <StepCadence
              form={form}
              patch={patch}
              errs={validity.errs}
              cost={cost}
              audienceCount={audienceCount}
            />
          ) : null}
          {step === 'booking' ? (
            <StepBooking
              form={form}
              patch={patch}
              errs={validity.errs}
              bookingPages={bookingPages}
              smsTemplates={smsTemplates}
              emailTemplates={emailTemplates}
              loaded={bookingLoaded}
            />
          ) : null}
          {step === 'brief' ? (
            <StepBrief form={form} patch={patch} errs={validity.errs} />
          ) : null}
          {step === 'review' ? (
            <StepReview
              form={form}
              patch={patch}
              audienceCount={audienceCount}
              cost={cost}
            />
          ) : null}

          {/* Soft warnings shared across steps */}
          {validity.errs.filter((e) => !e.hard).length > 0 ? (
            <div className="mt-4">
              <Callout tone="warn" title="Heads up">
                {validity.errs.filter((e) => !e.hard).map((e, i) => (
                  <div key={i}>· {e.msg}</div>
                ))}
              </Callout>
            </div>
          ) : null}

          {/* Footer */}
          <div className="flex items-center justify-between mt-7 pt-4 border-t border-white/[0.08]">
            <Button
              intent="ghost"
              size="sm"
              onClick={goBack}
              disabled={currentIndex === 0}
              className={currentIndex === 0 ? 'invisible' : ''}
            >
              <ArrowLeft size={12} strokeWidth={2.5} /> Back
            </Button>
            <div className="flex items-center gap-2">
              <span
                className={`text-[11px] ${canGoForward ? 'text-ok-text' : 'text-warn-text'}`}
              >
                {canGoForward
                  ? '✓ ready'
                  : `${validity.hardErrs.length} required`}
              </span>
              {step === 'review' ? (
                <Button
                  intent="secondary"
                  size="sm"
                  onClick={handleTestAgent}
                  disabled={submitting}
                  title="Saves the campaign as a draft, then opens a live WebRTC test call"
                >
                  <Mic size={12} strokeWidth={2} /> Test agent
                </Button>
              ) : null}
              <Button
                intent="secondary"
                size="sm"
                onClick={saveDraft}
                disabled={submitting}
              >
                Save as draft
              </Button>
              {step !== 'review' ? (
                <Button
                  intent="primary"
                  size="sm"
                  onClick={goNext}
                  disabled={!canGoForward}
                >
                  Next <ChevronRight size={12} strokeWidth={2.5} />
                </Button>
              ) : (
                <Button
                  intent="primary"
                  size="sm"
                  onClick={() => setConfirming(true)}
                  disabled={submitting}
                >
                  Launch · {audienceCount.toLocaleString()} will enrol
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>

      {confirming ? (
        <LaunchConfirmModal
          audienceCount={audienceCount}
          retryCount={form.retryCount}
          cost={cost}
          scheduleMode={form.scheduleMode}
          launchAt={form.launchAt}
          onCancel={() => setConfirming(false)}
          onConfirm={launch}
          submitting={submitting}
        />
      ) : null}

      {/* LT-534 — individual-contact picker (audience step). */}
      <ContactPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handlePickedContacts}
        title="Pick contacts for this campaign"
        description="Search and select individual contacts. They'll be saved as a static list and used as this campaign's audience."
        confirmLabel="Use as audience"
        busy={pickerBusy}
        error={pickerError}
      />

      {/* LT-543 — Test Agent (WebRTC) from the Review step. */}
      {testAgentOpen && testAgentId ? (
        <WebTestAgentModal
          isOpen={testAgentOpen}
          onClose={() => setTestAgentOpen(false)}
          campaignId={testAgentId}
        />
      ) : null}
    </PageShell>
  );
};

export default CampaignWizardPage;

// ===========================================================================
// Side rail — desktop
// ===========================================================================

interface SideRailProps {
  /** LT-658 — the active step list (varies by campaign type). */
  steps: StepDef[];
  step: StepId;
  setStep: (s: StepId) => void;
  form: FormState;
  audienceCount: number;
  /** Whether the current step passes validation. When true, the operator can
   * jump to any step via the rail; when false, only the current + earlier
   * steps are reachable (can't leave an invalid page forward). */
  canLeaveCurrent: boolean;
  /** Steps the operator has visited — issue badges only show for these. */
  visited: Set<StepId>;
}

const SideRail: React.FC<SideRailProps> = ({
  steps, step, setStep, form, audienceCount, canLeaveCurrent, visited,
}) => {
  const currentIdx = steps.find((s) => s.id === step)?.index ?? 0;
  return (
    <nav aria-label="Wizard steps" className="flex flex-col gap-1">
      {steps.map((s) => {
        const v = stepValidity(s.id, form, audienceCount);
        const active = s.id === step;
        // Free navigation: any step is reachable as long as the current page
        // is valid; going back to earlier steps is always allowed.
        const canNav = s.index <= currentIdx || canLeaveCurrent;
        // "Done" (green check) only once the operator has actually visited the
        // step AND it passes validation — otherwise steps with no required
        // fields (persona, retry, review) show green before they're reached,
        // which reads as "already complete". Edit mode marks every step visited
        // up front, so it still reflects the loaded campaign's true state.
        const done = !active && visited.has(s.id) && v.valid;
        // Only flag issues on steps the operator has actually visited.
        const hasIssues = !active && visited.has(s.id) && v.hardErrs.length > 0;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => canNav && setStep(s.id)}
            disabled={!canNav}
            className={`text-left p-3 rounded-ds-lg flex items-start gap-3 transition-colors ${
              active ? 'bg-surface-2' : 'bg-transparent'
            } ${canNav ? 'cursor-pointer hover:bg-white/[0.04]' : 'cursor-not-allowed opacity-60'}`}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-mono font-semibold flex-shrink-0 ${
                active
                  ? 'bg-accent text-white'
                  : done
                    ? 'bg-ok text-white'
                    : 'bg-surface-3 text-slate-400'
              }`}
            >
              {done ? <Check size={12} strokeWidth={2.5} /> : s.index + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className={`text-[13px] font-medium ${
                  active ? 'text-slate-50' : canNav ? 'text-slate-300' : 'text-slate-500'
                }`}
              >
                {s.label}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">{s.hint}</div>
              {hasIssues ? (
                <div className="text-[10px] text-warn-text mt-1">
                  {v.hardErrs.length} issue{v.hardErrs.length === 1 ? '' : 's'}
                </div>
              ) : null}
            </div>
          </button>
        );
      })}
    </nav>
  );
};

const MobileStepStrip: React.FC<SideRailProps> = ({
  steps, step, setStep, form, audienceCount, canLeaveCurrent, visited,
}) => {
  const currentIdx = steps.find((s) => s.id === step)?.index ?? 0;
  return (
    <div className="flex gap-1 p-2 rounded-ds-lg bg-surface-1 border border-white/[0.08] overflow-x-auto">
      {steps.map((s) => {
        const v = stepValidity(s.id, form, audienceCount);
        const active = s.id === step;
        const canNav = s.index <= currentIdx || canLeaveCurrent;
        const hasIssues = !active && visited.has(s.id) && v.hardErrs.length > 0;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => canNav && setStep(s.id)}
            disabled={!canNav}
            className={`flex-1 min-w-[80px] py-2 px-1 rounded-ds-md ${
              active ? 'bg-accent-tint text-accent-text' : 'text-slate-300'
            } ${canNav ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
          >
            <div className="text-[10px] font-mono text-slate-500">{s.index + 1}</div>
            <div className="text-[11px] font-medium mt-0.5 leading-none">{s.label}</div>
            {hasIssues ? (
              <div className="text-[9px] text-warn-text mt-0.5">{v.hardErrs.length}!</div>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

interface StepProps {
  form: FormState;
  patch: (p: Partial<FormState>) => void;
  errs: FieldErr[];
}

// ===========================================================================
// Step 0 — Campaign type (LT-658)
//
// The first decision: the campaign product shape. 'call_with_retry' is the
// legacy call-only campaign; 'call_with_booking' additionally books a meeting
// in-call and fires a confirmation SMS/email. The choice gates the conditional
// Booking step and is immutable once the campaign exists, so it leads.
// ===========================================================================

const CAMPAIGN_TYPE_OPTIONS: Array<{
  value: CampaignType;
  label: string;
  hint: string;
  Icon: typeof Phone;
}> = [
  {
    value: 'call_with_retry',
    label: 'Call with retry',
    hint: 'The agent calls each contact, retrying no-answers on your cadence. No booking — best for qualification, outreach, and follow-ups.',
    Icon: Phone,
  },
  {
    value: 'call_with_booking',
    label: 'Call + booking & confirmation',
    hint: 'The agent calls, books a meeting on your calendar during the call, then sends a confirmation by SMS and/or email. Best for demos and appointments.',
    Icon: CalendarCheck,
  },
];

const StepType: React.FC<StepProps & { locked?: boolean }> = ({ form, patch, locked = false }) => (
  <div className="grid gap-4 max-w-2xl">
    <div className="text-sm text-slate-300">
      {locked
        ? 'The campaign type is fixed once a campaign exists — it drives the in-call agent. To change it, create a new campaign.'
        : 'What should this campaign do? This is locked once the campaign is created, so pick the shape that matches the outcome you want.'}
    </div>
    <div
      className="grid gap-3 sm:grid-cols-2"
      role="radiogroup"
      aria-label="Campaign type"
    >
      {CAMPAIGN_TYPE_OPTIONS.map((opt) => {
        const active = form.campaignType === opt.value;
        const Icon = opt.Icon;
        // LT-658 — campaign_type is immutable. In edit mode the non-selected
        // option is disabled so the operator can't flip a persisted campaign's
        // shape (the PUT path never sends campaign_type anyway).
        const disabled = locked && !active;
        return (
          <label
            key={opt.value}
            className={`flex flex-col gap-2 p-4 rounded-ds-lg border transition-colors ${
              active ? 'border-accent/50 bg-accent-tint' : 'border-white/10 bg-surface-1'
            } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-white/[0.04]'}`}
          >
            <div className="flex items-center gap-2">
              <input
                type="radio"
                name="campaign-type"
                value={opt.value}
                checked={active}
                disabled={disabled}
                onChange={() => { if (!locked) patch({ campaignType: opt.value }); }}
                className="accent-accent"
                data-testid={`campaign-type-${opt.value}`}
              />
              <Icon
                size={16}
                strokeWidth={2.5}
                className={active ? 'text-accent-text' : 'text-slate-400'}
              />
              <span className="text-sm font-medium text-slate-100">{opt.label}</span>
            </div>
            <span className="text-xs text-slate-400">{opt.hint}</span>
          </label>
        );
      })}
    </div>
  </div>
);

// ===========================================================================
// Step 1 — Identity
// ===========================================================================

const StepIdentity: React.FC<StepProps> = ({ form, patch, errs }) => (
  <div className="grid gap-4 max-w-2xl md:grid-cols-2">
    <div className="md:col-span-2">
      <label className="block">
        <span className="text-xs text-slate-400">Campaign name <em className="not-italic text-accent-text font-mono text-[10px]">required</em></span>
        <Input
          value={form.name}
          onChange={(e) => patch({ name: e.target.value })}
          placeholder="e.g. Q2 AU Mid-Market Cold Push"
          maxLength={255}
          autoFocus
        />
      </label>
      <FieldErrInline field="name" errs={errs} />
    </div>
    <div className="md:col-span-2">
      <label className="block">
        <span className="text-xs text-slate-400">Description <span className="text-slate-500">· visible to teammates</span></span>
        <Textarea
          value={form.description}
          onChange={(e) => patch({ description: e.target.value })}
          rows={3}
          placeholder="Why is this campaign happening? What's the bet?"
        />
      </label>
    </div>
    <div>
      <label className="block">
        <span className="text-xs text-slate-400">Owner <em className="not-italic text-accent-text font-mono text-[10px]">required</em></span>
        <Input
          value={form.ownerName}
          disabled
          aria-label="Owner"
          title="Defaults to the signed-in user. Workspace admin reassignment lands later."
        />
      </label>
      <FieldErrInline field="owner" errs={errs} />
    </div>
    <div>
      <label className="block">
        <span className="text-xs text-slate-400">Enrolment closes <span className="text-slate-500">· optional</span></span>
        <Input
          type="datetime-local"
          value={form.enrolmentClosesAt}
          onChange={(e) => patch({ enrolmentClosesAt: e.target.value })}
        />
      </label>
    </div>
    <div className="md:col-span-2">
      {/* LT-413 (LT-405 §8.4): campaign_goals belongs on the Identity
          step as the strategic outcome anchor — distinct from the L2
          per-offer brief in step 2. */}
      <label className="block">
        <span className="text-xs text-slate-400">
          Campaign goals <em className="not-italic text-accent-text font-mono text-[10px]">required</em>
        </span>
        <Textarea
          value={form.campaignGoals}
          onChange={(e) => patch({ campaignGoals: e.target.value })}
          rows={2}
          placeholder='e.g. "Book 100 demos this quarter" or "Recover $50k in late receivables".'
          data-testid="identity-campaign-goals"
        />
      </label>
      <FieldErrInline field="campaignGoals" errs={errs} />
    </div>
  </div>
);

// ===========================================================================
// Step 3 — Audience (was step 2 pre-LT-413)
// ===========================================================================

interface StepAudienceProps extends StepProps {
  contactLists: ContactList[];
  audienceCount: number;
  audienceLoading: boolean;
  // LT-534 — opens the individual-contact picker. On confirm the parent
  // materialises the selection as a static list and selects it here.
  onPickContacts: () => void;
}

const StepAudience: React.FC<StepAudienceProps> = ({
  form, patch, errs, contactLists, audienceCount, audienceLoading, onPickContacts,
}) => {
  const audienceTone =
    audienceCount === 0 ? 'danger' : audienceCount > 5000 ? 'warn' : 'ok';
  const audienceLabel =
    audienceCount === 0 ? 'no matches' : audienceCount > 5000 ? 'large' : 'good';

  return (
    <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
      <div>
        <Tabs
          aria-label="Audience source"
          value={form.audienceMode}
          onChange={(v) => patch({ audienceMode: v as AudienceMode })}
          items={[
            { value: 'list',   label: 'Contact list' },
            { value: 'filter', label: 'Filter builder' },
          ]}
          className="mb-3"
        />

        {form.audienceMode === 'list' ? (
          <div className="flex flex-col gap-1.5">
            {/* LT-534 — pick individual contacts instead of a whole list.
                The selection is saved as an auto-created static list and
                selected below, so the rest of the flow is unchanged. */}
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="text-xs text-slate-500">
                Choose a saved list, or pick individual contacts.
              </div>
              <Button intent="secondary" size="sm" onClick={onPickContacts}>
                <UserPlus className="w-4 h-4" strokeWidth={2} />
                Pick contacts
              </Button>
            </div>
            {contactLists.length === 0 ? (
              <div className="text-xs text-slate-500 py-3">
                No contact lists yet. Pick individual contacts above, create a
                list from the Contacts page, or switch to Filter builder.
              </div>
            ) : null}
            {contactLists.map((cl) => {
              const selected = form.listId === cl.id;
              return (
                <button
                  key={cl.id}
                  type="button"
                  onClick={() => patch({ listId: cl.id })}
                  className={`p-3 rounded-ds-lg border flex items-center justify-between text-left transition-colors ${
                    selected
                      ? 'border-accent-border bg-accent-tint/30'
                      : 'border-white/[0.08] bg-surface-2 hover:bg-white/[0.04]'
                  }`}
                >
                  <div>
                    <div className="text-[13px] text-slate-100 font-medium">{cl.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                      {cl.list_type} · {cl.id.slice(0, 8)}
                    </div>
                  </div>
                  <Pill intent={cl.list_type === 'dynamic' ? 'info' : 'neutral'}>
                    {cl.list_type}
                  </Pill>
                </button>
              );
            })}
          </div>
        ) : null}

        {form.audienceMode === 'filter' ? (
          <FilterBuilder rules={form.filters} onChange={(r) => patch({ filters: r })} />
        ) : null}

        {/* JSON is no longer offered as a creation tab. This editor is only
            reachable in edit mode when an existing campaign's audience_filter
            is a raw blob that rehydrateAudience can't map to filter rules — we
            keep it so those legacy audiences stay editable rather than stranded. */}
        {form.audienceMode === 'json' ? (
          <Textarea
            value={form.json}
            onChange={(e) => patch({ json: e.target.value })}
            rows={6}
            spellCheck={false}
            className="font-mono text-xs"
            placeholder={'{\n  "lifecycle_stage": "qualified"\n}'}
          />
        ) : null}

        <div className="mt-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
            delivery rules
          </div>
          <div className="flex flex-col gap-2">
            <Checkbox
              checked={form.bypassCooldown}
              onChange={(e) => patch({ bypassCooldown: e.target.checked })}
              label="Allow more than one touch per contact per day"
              hint="Bypasses the daily cooldown for this campaign — a contact already called, emailed, or texted today can still be dispatched. Off (recommended): at most one outbound touch per contact per workspace-day."
            />
            {/* Opt-out isn't an operator toggle: unsubscribed / STOP'd
                contacts are on the workspace email + SMS suppression lists
                and are blocked at the dispatch boundary (LT-160 / LT-334),
                always-on for compliance. Surface that as a note rather than
                a checkbox that implies it can be turned off. */}
            <p className="text-[11px] text-slate-400 mt-1">
              Opted-out contacts (unsubscribed or replied STOP) are always
              skipped automatically — no setting needed.
            </p>
          </div>
        </div>

        <FieldErrInline field="audience" errs={errs} />
      </div>

      <div className="flex flex-col gap-3">
        <Card padding="tight">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
              estimated audience
            </div>
            <Pill intent={audienceTone}>{audienceLabel}</Pill>
          </div>
          <div className={`text-[28px] font-semibold font-mono ${
            audienceCount === 0 ? 'text-danger-text' : 'text-slate-50'
          }`}>
            {audienceLoading ? '…' : audienceCount.toLocaleString()}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            contacts will enrol at launch
            {form.audienceMode !== 'list' && audienceCount > 0
              ? ' · estimate, exact count computed at launch'
              : ''}
          </div>
        </Card>

        {form.audienceMode === 'list' ? null : (
          <Callout tone="info" title="Server-side preview pending">
            Filter / JSON modes show an estimate. The exact audience is
            resolved at launch from the audience query.
          </Callout>
        )}
      </div>
    </div>
  );
};

const FilterBuilder: React.FC<{
  rules: FilterRule[];
  onChange: (r: FilterRule[]) => void;
}> = ({ rules, onChange }) => (
  <div>
    {rules.map((r, i) => {
      const field = FILTER_FIELDS.find((x) => x.id === r.field) ?? FILTER_FIELDS[0];
      const update = (patch: Partial<FilterRule>) =>
        onChange(rules.map((x, j) => (j === i ? { ...x, ...patch } : x)));
      return (
        <div key={i} className="grid grid-cols-[1fr_88px_1fr_28px] gap-2 mb-2">
          <Select
            value={r.field}
            onChange={(e) => update({ field: e.target.value, value: '' })}
          >
            {FILTER_FIELDS.map((ff) => (
              <option key={ff.id} value={ff.id}>{ff.label}</option>
            ))}
          </Select>
          <Select
            value={r.op}
            onChange={(e) => update({ op: e.target.value as FilterRule['op'] })}
          >
            <option value="is">is</option>
            <option value="is_not">is not</option>
            {field.kind === 'number' ? (
              <>
                <option value="lt">&lt;</option>
                <option value="gt">&gt;</option>
              </>
            ) : null}
          </Select>
          {field.kind === 'enum' ? (
            <Select
              value={r.value}
              onChange={(e) => update({ value: e.target.value })}
            >
              <option value="">—</option>
              {(field.options ?? []).map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </Select>
          ) : (
            <Input
              type="text"
              inputMode={field.kind === 'number' ? 'numeric' : undefined}
              value={r.value}
              onChange={(e) => {
                const v = e.target.value;
                // Number fields accept only non-negative integers (and the
                // empty string while the user is mid-edit). Reject anything
                // else so "abcd" can't land in `Last touched (days ago)`.
                if (field.kind === 'number' && v !== '' && !/^\d+$/.test(v)) return;
                update({ value: v });
              }}
              placeholder={field.kind === 'number' ? 'numeric value' : 'value'}
            />
          )}
          <IconButton
            aria-label={`Remove rule ${i + 1}`}
            onClick={() => onChange(rules.filter((_, j) => j !== i))}
          >
            <X size={14} />
          </IconButton>
        </div>
      );
    })}
    <Button
      intent="ghost"
      size="sm"
      onClick={() =>
        onChange([...rules, { field: 'lifecycle_stage', op: 'is', value: '' }])
      }
    >
      <Plus size={12} strokeWidth={2.5} /> Add filter
    </Button>
  </div>
);

// ===========================================================================
// Step 4 — Retry Strategy (LT-532)
//
// Sequences are hidden from the UI (LT-527). Every campaign auto-attaches
// the workspace's implicit voice sequence (LT-528); the only knob the
// operator turns here is how many times and how far apart to retry a
// no-answer (LT-529). The implicit sequence is voice-only — follow-up
// emails / SMS are now on-demand, not part of the campaign cadence.
// ===========================================================================

// ===========================================================================
// LT-554 — Persona step. Symmetric agent creation in the wizard path
// (LT-536 decisions #5/#6). Three choices: use the workspace default agent,
// create a new one (L1 sub-form, same fields the upload path extracts), or
// pick an existing workspace agent (only when the catalogue has ≥ 2).
// ===========================================================================

interface StepPersonaProps extends StepProps {
  agents: Agent[];
}

const PERSONA_VOICE_GENDERS: Array<{ value: 'female' | 'male'; label: string }> = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
];
const PERSONA_VOICE_LANGUAGES: Array<{ value: 'en' | 'en_hi' | 'en_id'; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'en_hi', label: 'English + Hindi' },
  { value: 'en_id', label: 'English + Bahasa' },
];

const StepPersona: React.FC<StepPersonaProps> = ({ form, patch, errs, agents }) => {
  const canPickExisting = agents.length >= 2;

  // LT-663: resolve the agent this campaign will use so we can surface its
  // curated suggested input/output schema. 'create' has no agent yet.
  const selectedAgent = useMemo<Agent | null>(() => {
    if (form.personaMode === 'existing') {
      return agents.find((a) => a.id === form.existingAgentId) ?? null;
    }
    return null;
  }, [form.personaMode, form.existingAgentId, agents]);

  const suggestedInput = useMemo(
    () => normalizeOutputSchema(selectedAgent?.current_version?.suggested_input_schema),
    [selectedAgent],
  );
  const suggestedOutput = useMemo(
    () => normalizeOutputSchema(selectedAgent?.current_version?.suggested_output_schema),
    [selectedAgent],
  );
  const hasSuggestions = suggestedInput.length > 0 || suggestedOutput.length > 0;

  // When the workspace has fewer than two agents, "pick existing" is hidden —
  // fall back to the create-new sub-form so the step is always actionable.
  useEffect(() => {
    if (!canPickExisting && form.personaMode === 'existing') {
      patch({ personaMode: 'create' });
    }
  }, [canPickExisting, form.personaMode, patch]);

  // Picking an agent reseeds the campaign's editable input/output schema from
  // THAT agent's curated suggestions, so the fields shown always match the
  // chosen agent (an agent with no suggestions clears them). Fires only on an
  // actual pick, so an existing campaign's saved schema is preserved on load
  // and edits within one agent aren't clobbered.
  const selectAgent = (id: string) => {
    const a = agents.find((x) => x.id === id) ?? null;
    patch({
      existingAgentId: id,
      inputSchema: normalizeOutputSchema(a?.current_version?.suggested_input_schema),
      outputSchema: normalizeOutputSchema(a?.current_version?.suggested_output_schema),
    });
  };

  const choices: Array<{ value: PersonaMode; label: string; hint: string; show: boolean }> = [
    {
      value: 'existing',
      label: 'Pick an existing agent',
      hint: 'Reuse one of your workspace agents — its suggested data fields pre-fill below.',
      show: canPickExisting,
    },
    {
      value: 'create',
      label: 'Create a new agent for this campaign',
      hint: 'Describe how the agent should sound and behave. We build it and pin it to this campaign.',
      show: true,
    },
  ];

  return (
    <div className="grid gap-4 max-w-2xl">
      <div className="text-sm text-slate-300">
        Who makes the calls? Both campaign-creation paths build the same kind of
        agent — pick how you want to set this campaign’s up.
      </div>

      <div className="flex flex-col gap-2" role="radiogroup" aria-label="Agent choice">
        {choices.filter((c) => c.show).map((c) => {
          const active = form.personaMode === c.value;
          return (
            <label
              key={c.value}
              className={`flex gap-3 p-3 rounded-ds-lg border cursor-pointer transition-colors ${
                active ? 'border-accent/50 bg-accent-tint' : 'border-white/10 bg-surface-1'
              }`}
            >
              <input
                type="radio"
                name="persona-mode"
                value={c.value}
                checked={active}
                onChange={() => patch({ personaMode: c.value })}
                className="mt-1 accent-accent"
              />
              <span className="block">
                <span className="block text-sm text-slate-100">{c.label}</span>
                <span className="block text-xs text-slate-400">{c.hint}</span>
              </span>
            </label>
          );
        })}
      </div>

      {/* Create-new sub-form */}
      {form.personaMode === 'create' ? (
        <div className="grid gap-3 p-3 rounded-ds-lg border border-white/[0.08] bg-surface-2">
          <div>
            <label className="block">
              <span className="text-xs text-slate-400">
                Agent name <em className="not-italic text-accent-text font-mono text-[10px]">required</em>
              </span>
              <Input
                value={form.agentName}
                onChange={(e) => patch({ agentName: e.target.value })}
                placeholder="e.g. Riley — outbound discovery"
                maxLength={255}
                data-testid="persona-agent-name"
              />
            </label>
            <FieldErrInline field="agentName" errs={errs} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-400">Voice gender</span>
              <Select
                value={form.agentVoiceGender}
                onChange={(e) => patch({ agentVoiceGender: e.target.value as FormState['agentVoiceGender'] })}
              >
                {PERSONA_VOICE_GENDERS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </label>
            <label className="block">
              <span className="text-xs text-slate-400">Voice language</span>
              <Select
                value={form.agentVoiceLanguage}
                onChange={(e) => patch({ agentVoiceLanguage: e.target.value as FormState['agentVoiceLanguage'] })}
              >
                {PERSONA_VOICE_LANGUAGES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-slate-400">Tone</span>
            <Input
              value={form.agentTone}
              onChange={(e) => patch({ agentTone: e.target.value })}
              placeholder="e.g. warm, consultative, never pushy"
            />
          </label>
          <label className="block">
            <span className="text-xs text-slate-400">Agent persona</span>
            <Textarea
              value={form.agentPersona}
              onChange={(e) => patch({ agentPersona: e.target.value })}
              rows={2}
              placeholder="Who the agent is on the call — e.g. “I'm calling from Acme to follow up about…”"
            />
          </label>
          <label className="block">
            <span className="text-xs text-slate-400">Objection-handling style</span>
            <Textarea
              value={form.agentObjectionStyle}
              onChange={(e) => patch({ agentObjectionStyle: e.target.value })}
              rows={2}
              placeholder="How to react to pushback — e.g. acknowledge, offer a callback, never argue."
            />
          </label>
          <p className="text-[11px] text-slate-500">
            Voice IDs are chosen automatically from the gender + language you pick.
          </p>
        </div>
      ) : null}

      {/* Pick-existing */}
      {form.personaMode === 'existing' && canPickExisting ? (
        <div className="p-3 rounded-ds-lg border border-white/[0.08] bg-surface-2">
          <label className="block">
            <span className="text-xs text-slate-400">
              Agent <em className="not-italic text-accent-text font-mono text-[10px]">required</em>
            </span>
            <Select
              value={form.existingAgentId}
              onChange={(e) => selectAgent(e.target.value)}
            >
              <option value="">Select an agent…</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.is_system ? ' (system)' : ''}
                </option>
              ))}
            </Select>
          </label>
          <FieldErrInline field="existingAgentId" errs={errs} />
        </div>
      ) : null}

      {/* LT-663 — data the agent works with, seeded from the selected agent's
          curated suggestions. Editable + addable; saved on the campaign. */}
      <div className="grid gap-3 mt-2">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
            Data fields{selectedAgent ? ` · ${selectedAgent.name}` : ''}
          </div>
          {hasSuggestions ? (
            <Button
              intent="ghost"
              size="sm"
              onClick={() => patch({ inputSchema: suggestedInput, outputSchema: suggestedOutput })}
            >
              <Sparkles size={12} strokeWidth={2.5} /> Use suggested fields
            </Button>
          ) : null}
        </div>
        <p className="text-xs text-slate-500">
          {hasSuggestions
            ? `${selectedAgent?.name ?? 'This agent'} suggests these fields. Accept, rephrase, or add your own — they're saved on this campaign, not the agent.`
            : 'Declare the per-contact data fed to the agent and the data to capture from each call. Choose an agent with suggested fields to pre-fill these.'}
        </p>
        <CampaignOutputSchemaEditor
          title="Data to provide"
          description="Per-contact fields fed to the agent at call time, resolved from each contact's data."
          icon={<ArrowDownToLine size={15} strokeWidth={2.5} />}
          accentClass="text-info-text"
          schema={form.inputSchema}
          onSchemaChange={(next) => patch({ inputSchema: next })}
        />
        <CampaignOutputSchemaEditor
          title="Data to capture"
          description="Fields the post-call analyser extracts from each call — each becomes a column in the campaign CSV export."
          icon={<ClipboardCheck size={15} strokeWidth={2.5} />}
          accentClass="text-ok-text"
          schema={form.outputSchema}
          onSchemaChange={(next) => patch({ outputSchema: next })}
        />
      </div>
    </div>
  );
};

interface StepCadenceProps extends StepProps {
  cost: CostBreakdown;
  audienceCount: number;
}

const StepCadence: React.FC<StepCadenceProps> = ({
  form, patch, errs, cost, audienceCount,
}) => (
  <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
    <div>
      <fieldset className="block" data-testid="retry-strategy">
        <legend className="text-sm text-slate-300 mb-1">
          Every contact gets one call plus up to {form.retryCount} retries.
          Set how many no-answer retries to attempt and how many days apart.
        </legend>
        <div className="flex flex-col gap-4 mt-3">
          <label className="block max-w-[12rem]">
            <span className="text-xs text-slate-400">Number of retries</span>
            <Select
              value={String(form.retryCount)}
              onChange={(e) => {
                const n = Number(e.target.value);
                patch({
                  retryCount: n,
                  retryDelaysDays: resizeRetryDelays(form.retryDelaysDays, n),
                });
              }}
              aria-label="Number of retries"
              data-testid="retry-count"
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </Select>
          </label>
          <div className="flex flex-wrap gap-3">
            {form.retryDelaysDays.map((delay, i) => (
              <label key={i} className="block">
                <span className="text-xs text-slate-400">Retry {i + 1}</span>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    value={String(delay)}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      patch({
                        retryDelaysDays: form.retryDelaysDays.map((d, j) =>
                          j === i ? v : d),
                      });
                    }}
                    className="w-20"
                    data-testid={`retry-delay-${i}`}
                  />
                  <span className="text-xs text-slate-500">days</span>
                </div>
              </label>
            ))}
          </div>
        </div>
      </fieldset>

      <FieldErrInline field="cadence" errs={errs} />
    </div>

    <div className="flex flex-col gap-3">
      <Card padding="tight">
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
          attempts per contact
        </div>
        <div className="text-[22px] font-semibold font-mono mt-1 text-slate-50">
          {1 + form.retryCount}
          <span className="text-[13px] text-slate-500"> calls</span>
        </div>
        <div className="text-[11px] text-slate-500">
          1 initial + {form.retryCount} retries
        </div>
      </Card>

      <Card padding="tight">
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
          est. total calls
        </div>
        <div className="text-[22px] font-semibold font-mono mt-1 text-slate-50">
          {cost.callCount.toLocaleString()}
        </div>
        <div className="text-[11px] text-slate-500">
          {audienceCount.toLocaleString()} contacts × {1 + form.retryCount} calls
        </div>
        <div className="text-[11px] text-slate-500 mt-1">
          delays{' '}
          <span className="font-mono text-slate-300">
            {form.retryDelaysDays.join('/')}d
          </span>
        </div>
      </Card>

      <Card padding="tight">
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
          credits · upper bound
        </div>
        <div className="text-[22px] font-semibold font-mono mt-1 text-slate-50">
          {formatCredits(cost.totalCredits)}
          <span className="text-[13px] text-slate-500"> credits</span>
        </div>
        <div className="text-[11px] text-slate-500">
          Estimate only — you will be charged for actual usage
        </div>
      </Card>
    </div>
  </div>
);

// ===========================================================================
// Step — Booking & confirmation (LT-658)
//
// Shown only for call_with_booking campaigns. Collects the booking page the
// agent books against, plus the post-call confirmation channels (SMS / email).
// A channel is "on" when its toggle is enabled AND a template is picked — there
// are no separate boolean columns; the campaign just carries the template id
// (or null). At least one confirmation channel is required. The confirmation
// templates are REGULAR templates (they confirm an already-booked meeting via
// merge tokens), so we list ALL active templates for the channel.
// ===========================================================================

interface StepBookingProps extends StepProps {
  bookingPages: BookingPage[];
  smsTemplates: SmsTemplate[];
  emailTemplates: EmailTemplate[];
  /** Whether the catalogue fetch has been kicked off / resolved. Drives the
   *  "loading…" vs "no booking pages yet" distinction. */
  loaded: boolean;
}

const StepBooking: React.FC<StepBookingProps> = ({
  form, patch, errs, bookingPages, smsTemplates, emailTemplates, loaded,
}) => {
  const smsOn = form.smsConfirmEnabled;
  const emailOn = form.emailConfirmEnabled;
  return (
    <div className="grid gap-6 max-w-2xl">
      {/* Booking page */}
      <div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
          booking page
        </div>
        {bookingPages.length === 0 ? (
          <Callout tone="warn" title="No booking pages yet">
            {loaded
              ? 'A booking campaign books meetings against a booking page. '
              : 'Loading your booking pages… '}
            Create one, then come back to finish this campaign.{' '}
            <Link
              to="/settings/booking-pages"
              className="text-accent-text underline hover:text-accent"
            >
              Manage booking pages →
            </Link>
          </Callout>
        ) : (
          <label className="block">
            <span className="text-xs text-slate-400">
              Booking page <em className="not-italic text-accent-text font-mono text-[10px]">required</em>
            </span>
            <Select
              value={form.bookingPageId}
              onChange={(e) => patch({ bookingPageId: e.target.value })}
              data-testid="booking-page-picker"
            >
              <option value="">Select a booking page…</option>
              {bookingPages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title || p.slug || p.id}
                </option>
              ))}
            </Select>
          </label>
        )}
        <FieldErrInline field="bookingPage" errs={errs} />
      </div>

      {/* Confirmation channels */}
      <div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">
          confirmation message
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Send the booked contact a confirmation. Turn on at least one channel
          and pick the template that confirms the meeting (date &amp; time are
          filled in from merge tokens).
        </p>

        {/* SMS */}
        <ConfirmationChannel
          icon={<MessageSquare size={14} strokeWidth={2.5} className="text-slate-400" />}
          label="SMS confirmation"
          enabled={smsOn}
          onToggle={(on) => patch({ smsConfirmEnabled: on })}
          testId="confirm-sms-toggle"
        >
          {smsTemplates.length === 0 ? (
            <PrereqHint
              loaded={loaded}
              what="SMS templates"
              to="/settings/templates?type=sms"
              linkLabel="Manage SMS templates →"
            />
          ) : (
            <label className="block">
              <span className="text-xs text-slate-400">SMS template</span>
              <Select
                value={form.smsTemplateId}
                onChange={(e) => patch({ smsTemplateId: e.target.value })}
                data-testid="confirm-sms-template"
              >
                <option value="">Select a template…</option>
                {smsTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Select>
              <FieldErrInline field="smsTemplate" errs={errs} />
            </label>
          )}
        </ConfirmationChannel>

        {/* Email */}
        <ConfirmationChannel
          icon={<Mail size={14} strokeWidth={2.5} className="text-slate-400" />}
          label="Email confirmation"
          enabled={emailOn}
          onToggle={(on) => patch({ emailConfirmEnabled: on })}
          testId="confirm-email-toggle"
        >
          {emailTemplates.length === 0 ? (
            <PrereqHint
              loaded={loaded}
              what="email templates"
              to="/settings/templates"
              linkLabel="Manage email templates →"
            />
          ) : (
            <label className="block">
              <span className="text-xs text-slate-400">Email template</span>
              <Select
                value={form.emailTemplateId}
                onChange={(e) => patch({ emailTemplateId: e.target.value })}
                data-testid="confirm-email-template"
              >
                <option value="">Select a template…</option>
                {emailTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Select>
              <FieldErrInline field="emailTemplate" errs={errs} />
            </label>
          )}
        </ConfirmationChannel>

        <FieldErrInline field="confirmation" errs={errs} />
      </div>
    </div>
  );
};

// One confirmation channel: a toggle (set/clear the template id) plus, when
// on, the channel's template picker (rendered as children).
const ConfirmationChannel: React.FC<{
  icon: React.ReactNode;
  label: string;
  enabled: boolean;
  onToggle: (on: boolean) => void;
  testId: string;
  children: React.ReactNode;
}> = ({ icon, label, enabled, onToggle, testId, children }) => (
  <div className="mb-3 p-3 rounded-ds-lg border border-white/[0.08] bg-surface-2">
    <Checkbox
      checked={enabled}
      onChange={(e) => onToggle(e.target.checked)}
      label={
        <span className="inline-flex items-center gap-1.5">{icon}{label}</span>
      }
      data-testid={testId}
    />
    {enabled ? <div className="mt-3">{children}</div> : null}
  </div>
);

// Prerequisite-missing hint shown inside a channel when the workspace has no
// templates for it — links to the relevant management page.
const PrereqHint: React.FC<{
  loaded: boolean;
  what: string;
  to: string;
  linkLabel: string;
}> = ({ loaded, what, to, linkLabel }) => (
  <div className="text-xs text-warn-text">
    {loaded ? `No active ${what} yet. ` : `Loading ${what}… `}
    Create one to use this channel.{' '}
    <Link to={to} className="text-accent-text underline hover:text-accent">
      {linkLabel}
    </Link>
  </div>
);

// ===========================================================================
// Step 2 — Campaign Brief (was step 4 "Goals + AI" pre-LT-413)
//
// Per LT-405 §8.4 the per-offer brief lives on Campaign as structured
// columns (LT-407 added them; LT-409 backfilled from the legacy ai_context
// JSONB). Tone moved up to L1 (`agent_versions.tone`) and is NOT in the
// brief surface — the agent's tone shows in the L2 preview as inherited.
// "Success metrics" was retired in favour of the structured KPI shape.
// ===========================================================================

const StepBrief: React.FC<StepProps> = ({ form, patch, errs }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const briefChars = estimateBriefChars(form.brief);
  const tokenPct = Math.min(100, Math.round((briefChars / AI_TOKEN_BUDGET) * 100));
  const tokensOver = tokenPct >= 90;

  const draftFromSequence = () => {
    // Local draft fill while POST /campaigns/brief/draft is unbuilt.
    // Mirrors the LT-413 brief shape so the affordance demonstrates the
    // structured fields an LLM call would return.
    const next: CampaignBriefDraft = { ...form.brief };
    if (!next.purpose) next.purpose = 'Help mid-market AU CTOs cut billing reconciliation from 3 days to 30 minutes via our integration.';
    if (!next.target_audience) next.target_audience = 'CTO or VP Engineering at 50–500 person AU SaaS / Logistics companies';
    if (!next.call_objectives) next.call_objectives = 'Qualify on integration scope, then book a 30-min discovery call.';
    if (!next.likely_objections) next.likely_objections = 'already have a tool\nbudget tight this quarter\nprefer to email';
    if (!next.scripts) next.scripts = 'open with the ROI question\nif they bring up budget, mention the 30-day trial';
    patch({ brief: next });
  };

  const updateKpi = (i: number, p: Partial<KpiCard>) => {
    patch({
      kpis: form.kpis.map((k, j) => (j === i ? { ...k, ...p } : k)),
    });
  };

  return (
    <div>
      <div className="mb-4 text-sm text-slate-300">
        <span className="font-semibold">Step 2 of 6: Campaign Brief.</span>{' '}
        What we&apos;re saying, who we&apos;re saying it to, and how we&apos;ll measure success.
      </div>

      {/* KPIs */}
      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
        2a · KPIs to track
      </div>
      <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(220px,1fr))]">
        {form.kpis.map((k, i) => (
          <div
            key={i}
            className="relative p-3 rounded-ds-lg border border-white/[0.08] bg-surface-2"
          >
            <Input
              value={k.name}
              onChange={(e) => updateKpi(i, { name: e.target.value })}
              placeholder="KPI name"
              className="mb-2"
            />
            <div className="grid grid-cols-[40px_1fr_56px] gap-1">
              <Input
                value={k.prefix}
                onChange={(e) => updateKpi(i, { prefix: e.target.value })}
                placeholder="$"
                aria-label="Prefix"
              />
              <Input
                value={k.target}
                onChange={(e) => updateKpi(i, { target: e.target.value })}
                placeholder="target"
                inputMode="decimal"
                aria-label="Target value"
                className="font-mono"
              />
              <Input
                value={k.suffix}
                onChange={(e) => updateKpi(i, { suffix: e.target.value })}
                placeholder="%"
                aria-label="Suffix"
              />
            </div>
            <button
              type="button"
              onClick={() => patch({ kpis: form.kpis.filter((_, j) => j !== i) })}
              className="absolute top-1 right-1 p-1 text-slate-500 hover:text-slate-200"
              aria-label={`Remove KPI ${i + 1}`}
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            patch({
              kpis: [
                ...form.kpis,
                { name: '', target: '', prefix: '', suffix: '' },
              ],
            })
          }
          className="p-3 rounded-ds-lg border border-dashed border-white/[0.16] text-slate-400 text-xs hover:bg-white/[0.02]"
        >
          <Plus size={12} className="inline -mt-0.5" /> Add KPI
        </button>
      </div>

      {/* LT-663: the data-to-capture / data-to-provide schema editors moved
          to the Persona step, where they seed from the selected agent's
          curated suggestions. */}

      {/* LT-413: Brief — structured fields. Tone is L1 now and lives on
          the agent; the L2 preview block (below) shows tone as inherited
          rather than as an editable wizard field. */}
      <div className="flex items-center justify-between mt-6 mb-3">
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
          2b · Campaign Brief
        </div>
        <div className="flex items-center gap-2">
          <Button intent="secondary" size="sm" onClick={draftFromSequence}>
            <Sparkles size={12} strokeWidth={2.5} /> Draft from sequence + audience
          </Button>
          <Button
            intent="ghost"
            size="sm"
            onClick={() => setPreviewOpen((p) => !p)}
          >
            {previewOpen ? '↑ hide preview' : '↓ preview prompt'}
          </Button>
          <div className="flex items-center gap-1.5">
            <div className="w-14 h-1 bg-white/[0.06] rounded-full">
              <div
                className={`h-full rounded-full ${tokensOver ? 'bg-danger' : 'bg-accent'}`}
                style={{ width: `${tokenPct}%` }}
              />
            </div>
            <span className={`text-[10px] font-mono ${tokensOver ? 'text-danger-text' : 'text-slate-500'}`}>
              {briefChars}/{AI_TOKEN_BUDGET}
            </span>
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="block">
            <span className="text-xs text-slate-400">
              Why we&apos;re calling (purpose) <em className="not-italic text-accent-text font-mono text-[10px]">required</em>
            </span>
            <Textarea
              value={form.brief.purpose}
              onChange={(e) => patch({ brief: { ...form.brief, purpose: e.target.value } })}
              rows={2}
              placeholder="What outcome this campaign is selling. e.g. cut billing reconciliation from 3 days to 30 minutes."
              data-testid="wizard-brief-purpose"
            />
          </label>
          <FieldErrInline field="purpose" errs={errs} />
        </div>
        <div>
          <label className="block">
            <span className="text-xs text-slate-400">Target audience <em className="not-italic text-accent-text font-mono text-[10px]">required</em></span>
            <Input
              value={form.brief.target_audience}
              onChange={(e) => patch({ brief: { ...form.brief, target_audience: e.target.value } })}
              placeholder='e.g. "VP Sales at 50–200 person SaaS companies"'
              data-testid="wizard-brief-target-audience"
            />
          </label>
          <FieldErrInline field="target_audience" errs={errs} />
        </div>
        <div>
          <label className="block">
            <span className="text-xs text-slate-400">Call objectives <em className="not-italic text-accent-text font-mono text-[10px]">required</em></span>
            <Input
              value={form.brief.call_objectives}
              onChange={(e) => patch({ brief: { ...form.brief, call_objectives: e.target.value } })}
              placeholder='e.g. "Qualify, then book a 30-min discovery"'
              data-testid="wizard-brief-call-objectives"
            />
          </label>
          <FieldErrInline field="call_objectives" errs={errs} />
        </div>
        <div>
          <label className="block">
            <span className="text-xs text-slate-400">
              Likely objections <span className="text-slate-500">· one per line, max 20</span>
            </span>
            <Textarea
              value={form.brief.likely_objections}
              onChange={(e) => patch({ brief: { ...form.brief, likely_objections: e.target.value } })}
              rows={3}
              placeholder={'already have a tool\nbudget tight\nprefer email'}
              data-testid="wizard-brief-likely-objections"
            />
          </label>
        </div>
        <div>
          <label className="block">
            <span className="text-xs text-slate-400">
              Scripts <span className="text-slate-500">· one per line, max 20</span>
            </span>
            <Textarea
              value={form.brief.scripts}
              onChange={(e) => patch({ brief: { ...form.brief, scripts: e.target.value } })}
              rows={3}
              placeholder={'open with the ROI question\nif they bring up budget, mention the trial'}
              data-testid="wizard-brief-scripts"
            />
          </label>
        </div>
        <div className="md:col-span-2">
          <label className="block">
            <span className="text-xs text-slate-400">Additional notes</span>
            <Textarea
              value={form.brief.additional_notes}
              onChange={(e) => patch({ brief: { ...form.brief, additional_notes: e.target.value } })}
              rows={2}
              placeholder="Anything else the agent should know that doesn't fit the structured fields."
              data-testid="wizard-brief-additional-notes"
            />
          </label>
        </div>
      </div>

      {previewOpen ? (
        <div className="mt-4 p-3 rounded-ds-lg border border-white/[0.12] bg-surface-0 font-mono text-[11px] text-slate-300 leading-relaxed">
          <div className="text-slate-500 mb-1"># Layer 2 — campaign brief preview</div>
          {form.brief.purpose ? <div><span className="text-slate-500">PURPOSE:</span> {form.brief.purpose}</div> : null}
          {form.brief.target_audience ? <div><span className="text-slate-500">AUDIENCE:</span> {form.brief.target_audience}</div> : null}
          {form.brief.call_objectives ? <div><span className="text-slate-500">OBJECTIVES:</span> {form.brief.call_objectives}</div> : null}
          {form.brief.likely_objections ? <div><span className="text-slate-500">OBJECTIONS:</span> {form.brief.likely_objections.split('\n').filter(Boolean).length} listed</div> : null}
          {form.brief.scripts ? <div><span className="text-slate-500">SCRIPTS:</span> {form.brief.scripts.split('\n').filter(Boolean).length} listed</div> : null}
          {form.brief.additional_notes ? <div><span className="text-slate-500">NOTES:</span> {form.brief.additional_notes.length} chars</div> : null}
          {/* Tone is L1; surfaced as inherited from the pinned agent at
              call-time, not editable here. */}
          <div className="text-slate-500 mt-1 italic">
            TONE: inherited from the pinned agent (L1)
          </div>
          {!form.brief.purpose ? <div className="text-slate-500 mt-1">(empty — fill the fields above to compose)</div> : null}
        </div>
      ) : null}
    </div>
  );
};

// ===========================================================================
// Step 5 — Review
// ===========================================================================

interface StepReviewProps {
  form: FormState;
  patch: (p: Partial<FormState>) => void;
  audienceCount: number;
  cost: CostBreakdown;
}

const StepReview: React.FC<StepReviewProps> = ({
  form, patch, audienceCount, cost,
}) => {
  const audienceLabel = (() => {
    if (form.audienceMode === 'list') {
      const cl = form.listId; return `list · ${cl.slice(0, 8) || 'none'}`;
    }
    if (form.audienceMode === 'filter') return 'filter rules';
    return 'json query';
  })();

  return (
    <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
      <div className="flex flex-col gap-2">
        <ReviewLine
          label="Type"
          value={
            form.campaignType === 'call_with_booking'
              ? 'call + booking & confirmation'
              : 'call with retry'
          }
          ok
        />
        {form.campaignType === 'call_with_booking' ? (
          <ReviewLine
            label="Booking"
            value={(() => {
              const channels = [
                form.smsConfirmEnabled && form.smsTemplateId ? 'SMS' : null,
                form.emailConfirmEnabled && form.emailTemplateId ? 'email' : null,
              ].filter(Boolean);
              if (!form.bookingPageId) return 'no booking page';
              return `page · ${form.bookingPageId.slice(0, 8)} · confirm by ${
                channels.length ? channels.join(' + ') : 'no channel'
              }`;
            })()}
            ok={Boolean(
              form.bookingPageId
              && ((form.smsConfirmEnabled && form.smsTemplateId)
                || (form.emailConfirmEnabled && form.emailTemplateId)),
            )}
          />
        ) : null}
        <ReviewLine label="Name" value={form.name || '—'} ok={Boolean(form.name)} />
        <ReviewLine label="Owner" value={form.ownerName || '—'} />
        <ReviewLine
          label="Agent"
          value={
            form.personaMode === 'create'
              ? `new · ${form.agentName.trim() || 'unnamed'}`
              : form.personaMode === 'existing'
                ? 'existing workspace agent'
                : 'workspace default'
          }
          ok={form.personaMode !== 'create' || Boolean(form.agentName.trim())}
        />
        <ReviewLine
          label="Audience"
          value={`${audienceCount.toLocaleString()} contacts · ${audienceLabel}`}
          ok={audienceCount > 0}
          sub={form.bypassCooldown ? 'daily cooldown bypassed' : 'daily cooldown on'}
        />
        <ReviewLine
          label="Retry strategy"
          value={`1 call + ${form.retryCount} retries, delays ${form.retryDelaysDays.join('/')}d`}
          ok
        />
        <ReviewLine
          label="Goals"
          value={form.kpis.length > 0
            ? form.kpis
                .filter((k) => k.name)
                .map((k) => `${k.name} → ${k.prefix}${k.target}${k.suffix}`)
                .join(' · ') || 'none'
            : 'none'}
        />
        <ReviewLine
          label="Brief"
          value={form.brief.purpose
            ? `written · ${estimateBriefChars(form.brief)}/${AI_TOKEN_BUDGET} chars`
            : 'empty'}
          ok={Boolean(form.brief.purpose)}
          warn={!form.brief.purpose}
        />
        <ReviewLine
          label="Closes"
          value={form.enrolmentClosesAt || 'no end date'}
        />

        <div className="mt-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
            schedule
          </div>
          <div className="inline-flex gap-1 p-0.5 bg-surface-2 rounded-ds-lg border border-white/[0.08]">
            {(['now', 'later'] as const).map((m) => (
              <Button
                key={m}
                size="sm"
                intent={form.scheduleMode === m ? 'primary' : 'ghost'}
                onClick={() => patch({ scheduleMode: m })}
              >
                {m === 'now' ? 'Launch now' : 'Schedule for later'}
              </Button>
            ))}
          </div>
          {form.scheduleMode === 'later' ? (
            <div className="mt-2">
              <Input
                type="datetime-local"
                value={form.launchAt}
                onChange={(e) => patch({ launchAt: e.target.value })}
                className="w-60"
              />
              <p className="text-[11px] text-warn-text mt-1">
                Scheduled launches aren't dispatched server-side yet — campaign
                stays in draft until you launch manually.
              </p>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Card padding="tight">
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
            credits · upper bound
          </div>
          <div className="text-[26px] font-semibold font-mono mt-1 text-slate-50">
            {formatCredits(cost.totalCredits)}
            <span className="text-[14px] text-slate-500"> credits</span>
          </div>
          <div className="text-[11px] text-slate-500">
            Estimate only — you will be charged for actual usage
          </div>
          <div className="h-px bg-white/[0.08] my-3" />
          <div className="flex flex-col gap-1 text-[11px]">
            <CostRow
              label={`Voice calls (${cost.callCount.toLocaleString()})`}
              value={cost.voiceCredits}
            />
            {cost.smsCount > 0 ? (
              <CostRow
                label={`SMS (${cost.smsCount.toLocaleString()})`}
                value={cost.smsCredits}
              />
            ) : null}
            {cost.emailCount > 0 ? (
              <CostRow
                label={`Email (${cost.emailCount.toLocaleString()})`}
                value={cost.emailCredits}
              />
            ) : null}
          </div>
        </Card>

        <Callout tone="info" title="What launch does">
          <ol className="pl-4 list-decimal text-[11px] text-slate-300 space-y-1 mt-1">
            <li>{audienceCount.toLocaleString()} contacts snapshot into enrolments</li>
            <li>
              Each contact gets 1 call plus up to {form.retryCount} retries
              ({form.retryDelaysDays.join('/')}d apart)
            </li>
            <li>
              First call dispatches{' '}
              {form.scheduleMode === 'later' && form.launchAt
                ? `at ${form.launchAt}`
                : 'within 5 minutes'}
            </li>
            <li>Audit entry written</li>
          </ol>
        </Callout>
      </div>
    </div>
  );
};

const ReviewLine: React.FC<{
  label: string;
  value: string;
  sub?: string;
  ok?: boolean;
  warn?: boolean;
}> = ({ label, value, sub, ok, warn }) => (
  <div className="px-3 py-2.5 rounded-ds-md border border-white/[0.08] bg-surface-2">
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
        {label}
      </span>
      <span
        className={`text-xs font-medium text-right ${
          warn
            ? 'text-warn-text'
            : ok === false
              ? 'text-danger-text'
              : 'text-slate-100'
        }`}
      >
        {value}
      </span>
    </div>
    {sub ? <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div> : null}
  </div>
);

const CostRow: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-400">{label}</span>
    <span className="font-mono">{formatCredits(value)} cr</span>
  </div>
);

// ===========================================================================
// Launch confirm modal — type "launch" to enable
// ===========================================================================

interface LaunchConfirmModalProps {
  audienceCount: number;
  retryCount: number;
  cost: CostBreakdown;
  scheduleMode: 'now' | 'later';
  launchAt: string;
  onCancel: () => void;
  onConfirm: () => void;
  submitting: boolean;
}

const LaunchConfirmModal: React.FC<LaunchConfirmModalProps> = ({
  audienceCount, retryCount, cost, scheduleMode, launchAt,
  onCancel, onConfirm, submitting,
}) => {
  const [typed, setTyped] = useState('');
  const ok = typed.trim().toLowerCase() === 'launch';

  // ESC to cancel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="launch-confirm-title"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-ds-xl border border-white/[0.12] bg-surface-1 p-5 shadow-ds-e3"
      >
        <div id="launch-confirm-title" className="text-lg font-semibold text-slate-50 mb-2">
          Launch this campaign?
        </div>
        <p className="text-sm text-slate-400 mb-3">
          You're about to enrol <strong className="text-slate-100">{audienceCount.toLocaleString()} contacts</strong>{' '}
          — each gets <strong className="text-slate-100">1 call plus up to {retryCount} retries</strong>. The first call dispatches{' '}
          {scheduleMode === 'later' && launchAt ? `at ${launchAt}` : 'within 5 minutes'}.
          {' '}Estimated upper-bound cost:{' '}
          <strong className="font-mono text-slate-100">{formatCredits(cost.totalCredits)} credits</strong>.
        </p>
        <Callout tone="warn" title="One-way door">
          You can pause or force-end the campaign later, but contacts already
          touched will remember the touch. Stop now and Save as draft if you're
          unsure.
        </Callout>
        <label className="block mt-4">
          <span className="text-xs text-slate-400">
            Type <code className="text-accent-text font-mono">launch</code> to confirm
          </span>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="font-mono"
            autoFocus
            aria-label="Type launch to confirm"
          />
        </label>
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button intent="ghost" size="sm" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            intent="primary"
            size="sm"
            onClick={onConfirm}
            disabled={!ok || submitting}
          >
            {submitting ? 'Launching…' : `Launch · ${audienceCount.toLocaleString()} will enrol`}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ===========================================================================
// Inline field error
// ===========================================================================

const FieldErrInline: React.FC<{ field: string; errs: FieldErr[] }> = ({ field, errs }) => {
  const hits = errs.filter((e) => e.field === field);
  if (hits.length === 0) return null;
  return (
    <div className="mt-1 flex flex-col gap-0.5">
      {hits.map((e, i) => (
        <div
          key={i}
          className={`text-[11px] ${e.hard ? 'text-danger-text' : 'text-warn-text'}`}
        >
          {e.hard ? '✗' : '!'} {e.msg}
        </div>
      ))}
    </div>
  );
};
